/**
 * editor.js — CodeMirror 6 editor + Web Worker bridge for tau-cam.
 *
 * Adapted from tau-state demo: dark theme, syntax decorations, error gutter,
 * hover inspection via editor_worker.
 */

import { EditorState } from '@codemirror/state';
import { EditorView, ViewPlugin, Decoration, WidgetType, keymap, lineNumbers, gutter, GutterMarker, drawSelection } from '@codemirror/view';
import { defaultKeymap, indentMore, indentLess, history, historyKeymap, undo, redo, cursorLineUp, cursorLineDown, cursorCharLeft, cursorCharRight } from '@codemirror/commands';
import { StateField, StateEffect, RangeSet, Compartment } from '@codemirror/state';
import { autocompletion, acceptCompletion, startCompletion, moveCompletionSelection, selectedCompletion, completionStatus } from '@codemirror/autocomplete';

// ── Effects & StateField for syntax decorations ──

const setDecorations = StateEffect.define();

const decorationField = StateField.define({
  create() { return Decoration.none; },
  update(decos, tr) {
    for (const e of tr.effects) {
      if (e.is(setDecorations)) return e.value;
    }
    return decos.map(tr.changes);
  },
  provide: f => EditorView.decorations.from(f),
});

// ── Highlighted token (hover) ──

const setHighlight = StateEffect.define();

const highlightField = StateField.define({
  create() { return Decoration.none; },
  update(decos, tr) {
    for (const e of tr.effects) {
      if (e.is(setHighlight)) return e.value;
    }
    return decos.map(tr.changes);
  },
  provide: f => EditorView.decorations.from(f),
});

// ── Dark theme ──

const darkTheme = EditorView.theme({
  '&': {
    backgroundColor: 'var(--bg-body)',
    color: 'var(--text-primary)',
    fontSize: '16px',
    lineHeight: '1.6',
    fontFamily: "'Cascadia Mono', 'Cascadia Code', 'SF Mono', 'Fira Code', 'Consolas', monospace",
    border: '1px solid var(--border-medium)',
    borderRadius: '4px',
  },
  '.cm-content': {
    caretColor: 'var(--color-success)',
    padding: '10px',
    fontFamily: 'inherit',
  },
  '&.cm-focused .cm-cursor': {
    borderLeftColor: 'var(--color-success)',
  },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground': {
    background: 'var(--editor-selection) !important',
  },
  '&.cm-focused': {
    outline: 'none',
    borderColor: 'var(--editor-focus-border)',
  },
  '.cm-gutters': {
    backgroundColor: 'var(--bg-code)',
    color: 'var(--text-faint)',
    borderRight: '1px solid var(--border-subtle)',
    fontSize: 'inherit',
    lineHeight: 'inherit',
  },
  '.cm-activeLine': {
    backgroundColor: 'var(--editor-active-line)',
  },
  '.cm-activeLineGutter': {
    backgroundColor: 'var(--editor-active-line)',
  },
  '.cm-error-gutter': {
    width: '0.8em',
    paddingLeft: '1px',
  },
  '.cm-lineNumbers .cm-gutterElement': {
    padding: '0 0.3em 0 0.7em',
    minWidth: '0',
    fontSize: 'inherit',
    lineHeight: 'inherit',
  },
  '.cm-tooltip': {
    border: 'none !important',
  },
  '.cm-tooltip.cm-tooltip-autocomplete': {
    background: 'var(--bg-code)',
    border: '1px solid var(--border-medium) !important',
    borderRadius: '4px',
    fontFamily: 'inherit',
    fontSize: '13px',
    boxShadow: '0 4px 16px var(--accent-shadow)',
  },
  '.cm-tooltip-autocomplete ul': {
    fontFamily: 'inherit',
  },
  '.cm-tooltip-autocomplete ul li': {
    padding: '3px 10px !important',
    color: 'var(--text-muted)',
    lineHeight: '1.5',
  },
  '.cm-tooltip-autocomplete ul li[aria-selected]': {
    background: 'var(--completion-selected-bg) !important',
    color: 'var(--completion-selected-text) !important',
  },
  '.cm-completionLabel': {
    color: 'var(--text-primary)',
  },
  'li[aria-selected] .cm-completionLabel': {
    color: 'var(--completion-selected-text) !important',
  },
  '.cm-completionDetail': {
    color: 'var(--text-dim)',
    fontStyle: 'normal',
    marginLeft: 'auto',
    paddingLeft: '12px',
    fontSize: '11px',
  },
  'li[aria-selected] .cm-completionDetail': {
    color: 'var(--completion-selected-text) !important',
    fontWeight: 'bold',
    fontSize: 'inherit',
  },
  '.cm-completionMatchedText': {
    textDecoration: 'none',
    color: 'var(--accent)',
  },
  'li[aria-selected] .cm-completionMatchedText': {
    color: 'inherit',
  },
  // Error line + inline message form one box
  '.cm-error-line': {
    background: 'var(--accent-bg)',
    borderTop: '1px solid var(--accent)',
    borderLeft: '1px solid var(--accent)',
    borderRight: '1px solid var(--accent)',
    borderRadius: '3px 3px 0 0',
  },
  '.cm-inline-error': {
    color: 'var(--accent)',
    fontSize: '11px',
    fontStyle: 'italic',
    padding: '0 10px 1px',
    lineHeight: '1.3',
    background: 'var(--accent-bg)',
    borderBottom: '1px solid var(--accent)',
    borderLeft: '1px solid var(--accent)',
    borderRight: '1px solid var(--accent)',
    borderRadius: '0 0 3px 3px',
  },
}, { dark: true });

const tabSize = EditorState.tabSize.of(2);

// ── Error gutter markers ──

class ErrorMarker extends GutterMarker {
  constructor(title) { super(); this.title = title; }
  toDOM() {
    const el = document.createElement('span');
    el.textContent = '!';
    el.style.color = 'var(--color-error)';
    el.style.fontWeight = '700';
    el.title = this.title;
    return el;
  }
}

const setErrorMarkers = StateEffect.define();

const errorMarkerField = StateField.define({
  create() { return RangeSet.empty; },
  update(markers, tr) {
    for (const e of tr.effects) {
      if (e.is(setErrorMarkers)) return e.value;
    }
    return markers.map(tr.changes);
  },
});

const errorGutter = gutter({
  class: 'cm-error-gutter',
  markers: v => v.state.field(errorMarkerField),
});

// ── Inline error widgets (option 1) ──

class InlineErrorWidget extends WidgetType {
  constructor(text) { super(); this.text = text; }
  toDOM() {
    const el = document.createElement('div');
    el.className = 'cm-inline-error';
    el.textContent = '  ' + this.text;
    return el;
  }
  eq(other) { return this.text === other.text; }
}

const setInlineErrors = StateEffect.define();

const inlineErrorField = StateField.define({
  create() { return Decoration.none; },
  update(decos, tr) {
    for (const e of tr.effects) {
      if (e.is(setInlineErrors)) return e.value;
    }
    return decos.map(tr.changes);
  },
  provide: f => EditorView.decorations.from(f),
});

// ── Live loop bar widgets ──
//
// Decorations set once at Run via StateEffect, mapped through edits automatically.
// Canvas content drawn on rAF — never dispatches to CodeMirror.
// Widget elements cached globally by ID and reused across decoration rebuilds.

let loopEvents = new Map();     // loopName → [{color, time, dur, note}]
let stmtToLoop = new Map();     // stmtId → loopName (built at compile time)
let liveLoopBarRAF = 0;
let liveLoopBarRunning = false;

const ROLL_H = 20;
const SCOPE_W = 160; // scope waveform width (CSS px)
const SCOPE_H = 22;  // scope waveform height (CSS px)
const TOTAL_H = ROLL_H;
const ROLL_BEATS = 4; // visible beats in the roll
let rollBPM = 60; // updated from app.js

// Global widget element store — keyed by loop name, reused across rebuilds
const loopWidgetElements = {};

function getLoopWidget(name) {
  if (loopWidgetElements[name]) return loopWidgetElements[name];

  const wrap = document.createElement('span');
  wrap.className = 'live-loop-bar';
  wrap.setAttribute('aria-hidden', 'true');

  // Scope canvas — compact waveform on the live_loop line
  const scopeCanvas = document.createElement('canvas');
  scopeCanvas.className = 'll-scope';
  scopeCanvas.width = SCOPE_W * 2;
  scopeCanvas.height = SCOPE_H * 2;
  scopeCanvas.style.width = SCOPE_W + 'px';
  scopeCanvas.style.height = SCOPE_H + 'px';
  scopeCanvas.style.verticalAlign = 'middle';
  scopeCanvas.style.position = 'relative';
  scopeCanvas.style.top = '1px';
  scopeCanvas.style.marginRight = '6px';
  scopeCanvas.style.borderRadius = '4px';
  wrap.appendChild(scopeCanvas);

  const countEl = document.createElement('span');
  countEl.className = 'll-count';
  countEl.textContent = '0';
  wrap.appendChild(countEl);

  const canvas = document.createElement('canvas');
  canvas.className = 'll-canvas';
  canvas.height = ROLL_H * 2;
  canvas.style.height = ROLL_H + 'px';
  // Initial width — will be adjusted to fit available space on first draw
  canvas.width = 1200;
  canvas.style.width = '600px';
  wrap.appendChild(canvas);

  const entry = { wrap, canvas, scopeCanvas, countEl, ctx: canvas.getContext('2d'), scopeCtx: scopeCanvas.getContext('2d') };
  loopWidgetElements[name] = entry;
  return entry;
}

const setLoopBars = StateEffect.define();

class LiveLoopBarWidget extends WidgetType {
  constructor(name) {
    super();
    this.name = name;
  }
  toDOM() {
    return getLoopWidget(this.name).wrap;
  }
  eq(other) { return other instanceof LiveLoopBarWidget && this.name === other.name; }
  ignoreEvent() { return true; }
}

const liveLoopBarPlugin = ViewPlugin.fromClass(class {
  constructor() {
    this.decorations = Decoration.set([]);
  }
  update(update) {
    // Map positions through edits — widget stays attached to same code
    if (update.docChanged) {
      this.decorations = this.decorations.map(update.changes);
    }
    // Apply new decoration set from StateEffect (set once at Run)
    for (const tr of update.transactions) {
      for (const e of tr.effects) {
        if (e.is(setLoopBars)) {
          this.decorations = e.value;
        }
      }
    }
  }
}, {
  decorations: v => v.decorations,
});

function buildLoopBarDecorations() {
  if (!view) return;
  const doc = view.state.doc;
  const widgets = [];
  for (let i = 1; i <= doc.lines; i++) {
    const line = doc.line(i);
    const match = line.text.match(/^\s*live_loop\s+:(\w+)/);
    if (match) {
      widgets.push(
        Decoration.widget({
          widget: new LiveLoopBarWidget(match[1]),
          side: 1,
        }).range(line.to)
      );
    }
  }
  view.dispatch({ effects: setLoopBars.of(Decoration.set(widgets)) });
}

// rAF draw loop — mutates canvas directly, never dispatches to CodeMirror
function drawLoopBars() {
  const now = performance.now();
  const h2 = ROLL_H * 2;

  for (const name in loopWidgetElements) {
    const el = loopWidgetElements[name];
    const events = loopEvents.get(name) || [];
    const { ctx, canvas, countEl } = el;
    if (!ctx || !canvas.parentElement) continue;
    if (window._loopEventsEnabled === false) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (countEl) countEl.textContent = '';
      continue;
    }

    // Canvas uses fixed size set at creation — CSS overflow handles clipping
    const w2 = canvas.width;

    // Active count — events with no endTime are still alive
    const activeCount = events.filter(ev => !ev.endTime).length;
    if (countEl._last !== activeCount) {
      countEl.textContent = activeCount;
      countEl._last = activeCount;
    }

    // Clear
    ctx.clearRect(0, 0, w2, h2);

    // Note range for vertical scaling
    let minNote = 127, maxNote = 0;
    for (const ev of events) {
      if (ev.note > 0) { minNote = Math.min(minNote, ev.note); maxNote = Math.max(maxNote, ev.note); }
    }
    if (minNote > maxNote) { minNote = 48; maxNote = 84; }
    const noteRange = Math.max(maxNote - minNote, 12);

    // Time window scales with BPM
    const beatMs = 60000 / rollBPM;
    const rollWindowMs = ROLL_BEATS * beatMs;
    const tLeft = now - rollWindowMs;

    // Beat grid
    for (let b = 0; b <= ROLL_BEATS; b++) {
      const bx = (b / ROLL_BEATS) * w2;
      ctx.fillStyle = 'rgba(255,255,255,0.06)';
      ctx.fillRect(bx, 0, 1, h2);
    }

    // Draw events — bar grows in real-time until endTime is set
    for (const ev of events) {
      const evEnd = ev.endTime || now; // still alive = extends to now
      const startX = ((ev.time - tLeft) / rollWindowMs) * w2;
      const endX = ((evEnd - tLeft) / rollWindowMs) * w2;
      if (endX < 0 || startX > w2) continue;

      const x0 = Math.max(0, startX);
      const x1 = Math.min(w2, endX);
      if (x1 <= x0) continue;

      let y;
      if (ev.note < 0) {
        y = h2 - 5;
      } else {
        y = h2 - 7 - ((ev.note - minNote) / noteRange) * (h2 - 12);
      }

      const alive = !ev.endTime;
      const age = now - ev.time;

      const barH = 10;
      if (alive && age < 80) {
        // Trigger flash
        ctx.shadowColor = '#fff';
        ctx.shadowBlur = 14;
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#fff';
        ctx.fillRect(x0, y, x1 - x0, barH);
      } else if (alive) {
        // Still sounding — solid bright bar
        ctx.shadowColor = ev.color;
        ctx.shadowBlur = 4;
        ctx.globalAlpha = 1;
        ctx.fillStyle = ev.color;
        ctx.fillRect(x0, y, x1 - x0, barH);
      } else {
        // Ended — fade out over 800ms after endTime
        const sinceEnd = now - ev.endTime;
        const t = Math.min(1, sinceEnd / 800);
        ctx.shadowColor = ev.color;
        ctx.shadowBlur = (1 - t) * 4;
        ctx.globalAlpha = 1 - t * 0.7;
        ctx.fillStyle = ev.color;
        ctx.fillRect(x0, y, x1 - x0, barH);
      }
    }
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;

    // No playhead line — events appear at right edge naturally

    // ── Scope waveform (compact, on the live_loop line) ──
    if (window._sonicInstance && el.scopeCtx && window._scopeLoopsEnabled === false) {
      // Scopes disabled — clear canvas and reset history
      const sc = el.scopeCtx;
      sc.clearRect(0, 0, SCOPE_W * 2, SCOPE_H * 2);
      if (el._scopeHistory) el._scopeHistory.fill(0);
    }
    if (window._sonicInstance && el.scopeCtx && window._scopeLoopsEnabled !== false) {
      const loopScopes = window._readLoopScopes && window._readLoopScopes();
      const scopeData = loopScopes && loopScopes[name];
      const sc = el.scopeCtx;
      const sw = SCOPE_W * 2;
      const sh = SCOPE_H * 2;
      const smid = sh / 2;

      // Read scope colors from CSS (cached per element, invalidated on theme change)
      if (!el._scopeColors || el._scopeColorTheme !== document.body.className) {
        const cs = getComputedStyle(document.body);
        function parseRgb(prop, fallback) {
          const v = cs.getPropertyValue(prop).trim();
          const m = v.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
          return m ? [parseInt(m[1],16), parseInt(m[2],16), parseInt(m[3],16)] : fallback;
        }
        el._scopeColors = {
          accent: parseRgb('--color-loop-synth', [255, 20, 147]),
          wave: parseRgb('--color-loop-wave', [76, 131, 255]),
          outline: parseRgb('--color-loop-wave-outline', [255, 255, 255]),
        };
        el._scopeColorTheme = document.body.className;
        el._scopeBgRgb = null; // also invalidate bg cache
      }
      const [ar, ag, ab] = el._scopeColors.wave;
      const [pr, pg, pb] = el._scopeColors.accent;
      const [or, og, ob] = el._scopeColors.outline;

      // Scrolling history buffer — one peak per frame, slides left
      if (!el._scopeHistory) el._scopeHistory = new Float32Array(sw);
      const hist = el._scopeHistory;

      // Compute current peak and DC offset from scope data
      let curPeak = 0;
      let dcOffset = 0;
      if (scopeData && scopeData.active && scopeData.left) {
        const samples = scopeData.left;
        let sum = 0;
        for (let j = 0; j < samples.length; j++) {
          sum += samples[j];
          const a = Math.abs(samples[j]);
          if (a > curPeak) curPeak = a;
        }
        dcOffset = sum / samples.length;
      }
      // Clamp peak so it doesn't overflow the canvas
      curPeak = Math.min(curPeak, 1.0);

      // Shift history left and append new peak at right edge
      hist.copyWithin(0, 1);
      hist[sw - 1] = curPeak;

      // Soft fade — uses actual page background colour
      if (!el._scopeBgRgb) {
        const bgHex = getComputedStyle(document.body).getPropertyValue('--bg-body').trim() || '#0e0e10';
        const m = bgHex.match(/^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
        el._scopeBgRgb = m ? [parseInt(m[1],16), parseInt(m[2],16), parseInt(m[3],16)] : [14, 14, 16];
      }
      const [br, bg, bb] = el._scopeBgRgb;
      sc.fillStyle = `rgba(${br},${bg},${bb},0.45)`;
      sc.fillRect(0, 0, sw, sh);

      // Faint resting centreline — always visible so scope never looks dead
      sc.strokeStyle = `rgba(${ar},${ag},${ab},0.08)`;
      sc.lineWidth = 0.5;
      sc.beginPath();
      sc.moveTo(0, smid);
      sc.lineTo(sw, smid);
      sc.stroke();

      // Find max in visible history for adaptive scaling
      let histMax = 0;
      for (let c = 0; c < sw; c++) {
        if (hist[c] > histMax) histMax = hist[c];
      }
      const alive = histMax > 0.001;
      const intensity = Math.min(1, histMax * 3);

      // ── Pink scrolling peak history (fill + single edge stroke) ──
      const histA = alive ? 0.35 + intensity * 0.25 : 0.08;
      sc.fillStyle = `rgba(${pr},${pg},${pb},${histA})`;
      sc.beginPath();
      sc.moveTo(0, smid);
      for (let c = 0; c < sw; c++) {
        sc.lineTo(c, smid - hist[c] * smid * 0.92);
      }
      sc.lineTo(sw, smid);
      for (let c = sw - 1; c >= 0; c--) {
        sc.lineTo(c, smid + hist[c] * smid * 0.92);
      }
      sc.closePath();
      sc.fill();
      // Reuse same path for edge
      sc.strokeStyle = `rgba(${pr},${pg},${pb},${alive ? 0.45 : 0.1})`;
      sc.lineWidth = 1;
      sc.stroke();

      // ── Blue live waveform with white border ──
      if (scopeData && scopeData.active && scopeData.left && curPeak > 0.001) {
        const samples = scopeData.left;
        const step = samples.length / sw;
        const liveInt = Math.min(1, curPeak * 3);

        sc.lineJoin = 'round';
        sc.lineCap = 'round';
        sc.beginPath();
        for (let c = 0; c < sw; c++) {
          const s = Math.max(-1, Math.min(1, samples[Math.floor(c * step)] - dcOffset));
          const y = smid - s * smid * 0.8;
          if (c === 0) sc.moveTo(c, y);
          else sc.lineTo(c, y);
        }
        // Soft outline halo — thin stroke + shadowBlur gives the FX-pad glow
        // feel without the heavy bordered look.
        sc.shadowColor = `rgba(${or},${og},${ob},0.9)`;
        sc.shadowBlur = 6;
        sc.strokeStyle = `rgba(${or},${og},${ob},${0.18 + liveInt * 0.22})`;
        sc.lineWidth = 3.5;
        sc.stroke();
        // Main wave line — kept thin so it reads as a clean trace through the
        // glow, not a slab.
        sc.shadowBlur = 0;
        sc.strokeStyle = `rgba(${ar},${ag},${ab},${0.55 + liveInt * 0.3})`;
        sc.lineWidth = 1.75;
        sc.stroke();
      }
    }
  }
}

function startLiveLoopBars() {
  liveLoopBarRunning = true;
  buildLoopBarDecorations();
  if (liveLoopBarRAF) return;
  function tick() {
    if (!liveLoopBarRunning) return;
    liveLoopBarRAF = requestAnimationFrame(tick);
    drawLoopBars();
  }
  liveLoopBarRAF = requestAnimationFrame(tick);
}

function stopLiveLoopBars() {
  liveLoopBarRunning = false;
  if (liveLoopBarRAF) {
    cancelAnimationFrame(liveLoopBarRAF);
    liveLoopBarRAF = 0;
  }
  loopEvents.clear();
  stmtToLoop.clear();
  // Clear widget element cache
  for (const name in loopWidgetElements) delete loopWidgetElements[name];
  if (view) {
    view.dispatch({ effects: setLoopBars.of(Decoration.none) });
  }
}

// ── Build decorations from worker leaves + compile errors ──

function buildDecorations(leaves, errors, doc, showErrors) {
  const docLength = doc.length;
  const builder = [];
  for (let i = 0; i < leaves.length; i++) {
    const lf = leaves[i];
    if (lf.s >= docLength || lf.e > docLength) continue;
    if (lf.s >= lf.e) continue;

    const color = themeSyntaxColor(lf.color);
    let style = `color: ${color}`;
    // Comments in italic
    const lc = lf.color.toLowerCase();
    if (lc === '#5e5e5e' || lc === '#999999' || lc === '#999' || lc === '#aaaaaa' || lc === '#aaa') {
      style += '; font-style: italic';
    }
    // In HC mode: :symbols get yellow bg, numbers get white bg
    const isHC = document.body.classList.contains('theme-highcontrast');
    if (isHC) {
      const hc = lf.color.toUpperCase();
      const ch = lf.s < docLength ? doc.sliceString(lf.s, lf.s + 1) : '';
      if (hc === '#FF1493' && ch === ':') {
        style = 'color:#000; background:#ffff00; padding:1px 3px; border-radius:2px; font-weight:bold';
      } else if (hc === '#4C83FF' && /[0-9\-]/.test(ch)) {
        style = 'color:#000; background:#fff; padding:1px 3px; border-radius:2px; font-weight:bold';
      }
    }
    if (!isHC && lf.depth > 0) {
      const bg = Math.min(lf.depth * 0.025, 0.15);
      if (bg > 0.01) {
        const rgb = hexToRgb(color);
        style += `; background: rgba(${rgb[0]},${rgb[1]},${rgb[2]},${bg.toFixed(3)})`;
        style += `; border-radius: 2px; padding: 0 1px; margin: 0 -1px`;
      }
    }
    if (showErrors && lf.isError) {
      style += `; color: var(--text-muted); font-style: italic`;
    }

    builder.push(
      Decoration.mark({ attributes: { style, 'data-s': String(lf.s), 'data-e': String(lf.e) } })
        .range(lf.s, lf.e)
    );
  }

  // Add compile error underlines
  if (showErrors) {
    for (let i = 0; i < errors.length; i++) {
      const e = errors[i];
      if (e.line < 1 || e.line > doc.lines) continue;
      const startLine = doc.line(e.line);
      const from = startLine.from + e.col;
      const endLine = (e.endLine >= 1 && e.endLine <= doc.lines) ? doc.line(e.endLine) : startLine;
      let to = endLine.from + e.endCol;
      if (to <= from) to = Math.min(from + 1, endLine.to);
      if (from >= docLength || to > docLength) continue;
      builder.push(
        Decoration.mark({
          attributes: {
            style: 'color: var(--text-muted); font-style: italic',
            title: e.text,
          }
        }).range(from, to)
      );
    }
  }

  builder.sort((a, b) => a.from - b.from || a.to - b.to);
  return Decoration.set(builder, true);
}

function buildErrorGutterMarkers(errors, doc) {
  const markers = [];
  const seenLines = new Set();
  for (let i = 0; i < errors.length; i++) {
    const e = errors[i];
    if (e.line < 1 || e.line > doc.lines) continue;
    if (seenLines.has(e.line)) continue;
    seenLines.add(e.line);
    const lineObj = doc.line(e.line);
    markers.push(new ErrorMarker(e.text).range(lineObj.from));
  }
  markers.sort((a, b) => a.from - b.from);
  return RangeSet.of(markers);
}

function hexToRgb(hex) {
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}

// Remap WASM syntax colors for non-dark themes
function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return [h, s, l];
}

function hslToHex(h, s, l) {
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  let r, g, b;
  if (s === 0) { r = g = b = l; }
  else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  const toHex = v => Math.round(v * 255).toString(16).padStart(2, '0');
  return '#' + toHex(r) + toHex(g) + toHex(b);
}

// sRGB linearization for WCAG luminance
function linearize(v) {
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function relativeLuminance(r, g, b) {
  return 0.2126 * linearize(r / 255) + 0.7152 * linearize(g / 255) + 0.0722 * linearize(b / 255);
}

function themeSyntaxColor(hex) {
  if (document.body.classList.contains('theme-light')) {
    const [r, g, b] = hexToRgb(hex);
    let [h, s, l] = rgbToHsl(r, g, b);
    l = Math.min(l, 0.38);
    s = Math.min(s + 0.1, 1);
    return hslToHex(h, s, l);
  }
  if (document.body.classList.contains('theme-highcontrast')) {
    const hc = hex.toUpperCase();
    // White (keywords) → white
    if (hc === '#EDEDED' || hc === '#FFFFFF') return '#ffffff';
    // Pink/magenta family → yellow (bg only applied in buildDecorations for :prefixed symbols)
    if (hc === '#FF1493' || hc === '#E060C0' || hc === '#FF0080') return '#ffff00';
    // Blue family → green
    if (hc === '#4C83FF' || hc === '#50A0E0') return '#00ff80';
    // Green family → green
    if (hc === '#61CE3C' || hc === '#00EB6C' || hc === '#14B96F') return '#00ff80';
    // Gold/yellow → yellow
    if (hc === '#FBDE2D' || hc === '#FFD700') return '#ffff00';
    // Grey (comments) → muted
    if (hc === '#5E5E5E' || hc === '#999999') return '#aaaaaa';
    return '#ffffff';
  }
  return hex;
}

function escHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escAttr(s) {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

// ── Autocompletion (async, worker-driven) ──

// Sample names kept client-side (they're filesystem data, not compiler data)
const SAMPLE_NAMES = [
  'ambi_choir', 'ambi_dark_woosh', 'ambi_drone', 'ambi_glass_hum', 'ambi_glass_rub',
  'ambi_haunted_hum', 'ambi_lunar_land', 'ambi_piano', 'ambi_sauna', 'ambi_soft_buzz', 'ambi_swoosh',
  'bass_dnb_f', 'bass_drop_c', 'bass_hard_c', 'bass_hit_c', 'bass_thick_c',
  'bass_trance_c', 'bass_voxy_c', 'bass_voxy_hit_c', 'bass_woodsy_c',
  'bd_808', 'bd_ada', 'bd_boom', 'bd_chip', 'bd_fat', 'bd_gas', 'bd_haus',
  'bd_jazz', 'bd_klub', 'bd_mehackit', 'bd_pure', 'bd_sone', 'bd_tek', 'bd_zome', 'bd_zum',
  'drum_bass_hard', 'drum_bass_soft', 'drum_cowbell', 'drum_cymbal_closed',
  'drum_cymbal_hard', 'drum_cymbal_open', 'drum_cymbal_pedal', 'drum_cymbal_soft',
  'drum_heavy_kick', 'drum_roll', 'drum_snare_hard', 'drum_snare_soft',
  'drum_splash_hard', 'drum_splash_soft',
  'drum_tom_hi_hard', 'drum_tom_hi_soft', 'drum_tom_lo_hard', 'drum_tom_lo_soft',
  'drum_tom_mid_hard', 'drum_tom_mid_soft',
  'elec_beep', 'elec_bell', 'elec_blip', 'elec_blip2', 'elec_blup', 'elec_bong',
  'elec_chime', 'elec_cymbal', 'elec_filt_snare', 'elec_flip', 'elec_fuzz_tom',
  'elec_hi_snare', 'elec_hollow_kick', 'elec_lo_snare', 'elec_mid_snare',
  'elec_ping', 'elec_plip', 'elec_pop', 'elec_snare', 'elec_soft_kick',
  'elec_tick', 'elec_triangle', 'elec_twang', 'elec_twip', 'elec_wood',
  'glitch_bass_g', 'glitch_perc1', 'glitch_perc2', 'glitch_perc3', 'glitch_perc4',
  'glitch_perc5', 'glitch_robot1', 'glitch_robot2',
  'guit_e_fifths', 'guit_e_slide', 'guit_em9', 'guit_harmonics',
  'hat_bdu', 'hat_cab', 'hat_cats', 'hat_gem', 'hat_gnu', 'hat_gump',
  'hat_hier', 'hat_len', 'hat_mess', 'hat_metal', 'hat_noiz', 'hat_psych',
  'hat_raw', 'hat_sci', 'hat_snap', 'hat_star', 'hat_tap', 'hat_yosh', 'hat_zan', 'hat_zap', 'hat_zild',
  'loop_3d_printer', 'loop_amen', 'loop_amen_full', 'loop_breakbeat', 'loop_compus',
  'loop_drone_g_97', 'loop_electric', 'loop_garzul', 'loop_industrial',
  'loop_mehackit1', 'loop_mehackit2', 'loop_mika', 'loop_perc1', 'loop_perc2',
  'loop_safari', 'loop_tabla', 'loop_weirdo',
  'misc_burp', 'misc_cineboom', 'misc_crow',
  'perc_bell', 'perc_bell2', 'perc_door', 'perc_impact1', 'perc_impact2',
  'perc_snap', 'perc_snap2', 'perc_swash', 'perc_swoosh', 'perc_till',
  'ride_tri', 'ride_via',
  'sn_dolf', 'sn_dub', 'sn_generic', 'sn_zome',
  'tabla_dhec', 'tabla_ghe1', 'tabla_ghe2', 'tabla_ghe3', 'tabla_ghe4',
  'tabla_ghe5', 'tabla_ghe6', 'tabla_ghe7', 'tabla_ghe8',
  'tabla_ke1', 'tabla_ke2', 'tabla_ke3', 'tabla_na', 'tabla_na_o', 'tabla_na_s',
  'tabla_re', 'tabla_tas1', 'tabla_tas2', 'tabla_tas3',
  'tabla_te1', 'tabla_te2', 'tabla_te_m', 'tabla_te_ne',
  'tabla_tun1', 'tabla_tun2', 'tabla_tun3',
  'vinyl_backspin', 'vinyl_hiss', 'vinyl_rewind', 'vinyl_scratch',
];

let completionResolve = null;
let formatResolve = null;
let completionDocActive = false;
let lastSelectedLabel = null;

// Statement execution highlighting
//
// statementMap: updated on every recompile (for general use / syntax)
// flashMap:     snapshotted at Run time — used exclusively for flash lookups
//              so runtime IDs always match the compilation that produced the bytecode
let statementMap = new Map(); // id → { start, end }  (from latest compile)
let flashMap = new Map();     // id → { start, end }  (from the Run compilation)
const activeFlashes = new Map(); // id → { from, to, timer }
let flashRAF = 0;
let flashDirty = false;

function highlightStatement(id) {
  const entry = flashMap.get(id);
  if (!entry || !view) return;

  const docLen = view.state.doc.length;
  const start = Math.min(entry.start, docLen);
  const text = view.state.doc.sliceString(start, Math.min(start + 20, docLen));
  const kwEnd = text.search(/[^a-z_]/);
  const end = kwEnd > 0 ? start + kwEnd : start + text.length;
  if (end <= start) return;

  if (activeFlashes.has(id)) {
    clearTimeout(activeFlashes.get(id).timer);
  }

  const timer = setTimeout(() => {
    activeFlashes.delete(id);
    scheduleFlashUpdate();
  }, 100);

  activeFlashes.set(id, { from: start, to: end, timer });
  scheduleFlashUpdate();
}

function scheduleFlashUpdate() {
  if (flashDirty) return;
  flashDirty = true;
  flashRAF = requestAnimationFrame(applyFlashes);
}

function applyFlashes() {
  flashDirty = false;
  if (!view) return;
  const ranges = [];
  const docLen = view.state.doc.length;
  for (const { from, to } of activeFlashes.values()) {
    const f = Math.min(from, docLen);
    const t = Math.min(to, docLen);
    if (t > f) ranges.push(Decoration.mark({ class: 'stmt-flash' }).range(f, t));
  }
  ranges.sort((a, b) => a.from - b.from);
  view.dispatch({ effects: setHighlight.of(RangeSet.of(ranges)) });
}

// ── Fuzzy matching ──

function fuzzyMatch(pattern, text) {
  if (pattern.length === 0) return { score: 0, positions: [] };
  const lp = pattern.toLowerCase();
  const lt = text.toLowerCase();
  let pi = 0;
  const positions = [];
  let score = 0;
  let prev = -2;

  for (let i = 0; i < lt.length && pi < lp.length; i++) {
    if (lt[i] === lp[pi]) {
      positions.push(i);
      if (prev === i - 1) score += 5;
      if (i === 0 || text[i - 1] === '_' || text[i - 1] === ':') score += 10;
      score += 1;
      prev = i;
      pi++;
    }
  }

  if (pi < lp.length) return null;
  // Bonus for matching at or near the start (after optional :)
  const firstMatch = positions[0];
  const startPos = text[0] === ':' ? 1 : 0;
  if (firstMatch === startPos) score += 20;
  else score -= firstMatch * 2;
  // Tighter match span is better
  score -= (positions[positions.length - 1] - positions[0]) * 0.5;
  // Shorter labels preferred
  score -= text.length * 0.1;
  return { score, positions };
}

function makeFuzzyResult(from, allOptions, typed) {
  let filtered;
  if (!typed) {
    filtered = allOptions.map(opt => ({ ...opt, _matchPositions: [] }));
  } else {
    filtered = [];
    for (const opt of allOptions) {
      const m = fuzzyMatch(typed, opt.label);
      if (m) {
        filtered.push({ ...opt, _matchPositions: m.positions, boost: m.score });
      }
    }
    filtered.sort((a, b) => (b.boost || 0) - (a.boost || 0));
  }

  if (filtered.length === 0) return null;

  return {
    from,
    options: filtered,
    filter: false,
    getMatch: (completion) => {
      const pos = completion._matchPositions;
      if (!pos || pos.length === 0) return [];
      // CM6 expects [from, to) range pairs, not individual positions
      const ranges = [];
      let start = pos[0], end = pos[0] + 1;
      for (let i = 1; i < pos.length; i++) {
        if (pos[i] === end) { end++; }
        else { ranges.push(start, end); start = pos[i]; end = pos[i] + 1; }
      }
      ranges.push(start, end);
      return ranges;
    },
    update(current, mappedFrom, mappedTo, context) {
      const newTyped = context.state.sliceDoc(mappedFrom, context.pos);
      return makeFuzzyResult(mappedFrom, allOptions, newTyped);
    },
  };
}

function tauCompletion(ctx) {
  if (!worker || !workerReady) return null;
  // Suppress when TreeNav sample browser is active
  if (treeNav.active) return null;
  // Activate when typing a word/symbol, explicitly triggered, or after space following a value
  if (!ctx.explicit && !ctx.matchBefore(/[\w:\/"+]+/)) return null;
  // Don't trigger on keywords or empty/whitespace lines
  const line = ctx.state.doc.lineAt(ctx.pos);
  const lineText = line.text.slice(0, ctx.pos - line.from).trim();
  if (!lineText) return null;
  const wordMatch = ctx.matchBefore(/\b\w+$/);
  if (wordMatch && /^(do|end|else|true|false|and|or|if|stop)$/.test(wordMatch.text)) return null;
  if (/^\s*(do|end|else|true|false|and|or|if|stop)\s*$/.test(lineText)) return null;
  if (/\b(do|end|define)\s*$/.test(lineText)) return null;
  // Suppress when typing 'do' after a block opener (live_loop, times, in_thread, define, if)
  if (wordMatch && /^d/.test(wordMatch.text) && /\b(live_loop|times|in_thread|define|if)\b/.test(lineText)) return null;

  // Dynamic cue path completion for sync
  const before = line.text.slice(0, ctx.pos - line.from);
  if (/^\s*(?:sync|n\s*=\s*sync|\w+\s*=\s*sync)\s+/.test(before)) {
    const pathMatch = before.match(/(?:"|:|\/)([\/\w]*)$/);
    if (pathMatch && window._knownCuePaths && window._knownCuePaths.size > 0) {
      const from = ctx.pos - pathMatch[0].length;
      const paths = [...window._knownCuePaths];
      const items = paths.map(p => ({
        label: '"' + p + '"',
        type: 'constant',
        detail: 'cue event',
      }));
      if (items.length > 0) {
        return makeFuzzyResult(from, items, pathMatch[0]);
      }
    }
  }

  return new Promise((resolve) => {
    completionResolve = (msg) => {
      if (!msg.items || msg.items.length === 0) {
        // If worker returned a samples placeholder, fill in sample names client-side
        resolve(null);
        return;
      }

      // Check if worker asked us to supply sample names (via :_samples placeholder)
      const hasSamplesPlaceholder = msg.items.some(item => item.label === ':_samples');
      let allOptions;
      if (hasSamplesPlaceholder) {
        allOptions = [];
        // Built-in samples
        for (const s of SAMPLE_NAMES) {
          allOptions.push({ label: ':' + s, apply: ':' + s + ' ', type: 'sample', itemKind: 'sample', detail: '▶ SPC' });
        }
        // User samples from scanned directories
        if (window._userSampleIndex) {
          for (const p of Object.keys(window._userSampleIndex)) {
            allOptions.push({ label: '/' + p, apply: '/' + p + ' ', type: 'sample', itemKind: 'sample', detail: '▶ SPC' });
          }
        }
      } else {
        allOptions = msg.items.map(item => ({
          label: item.label,
          apply: item.label + ' ',
          type: item.kind === 'param' ? 'property' : item.kind === 'synth' ? 'constant' : item.kind === 'fx' ? 'constant' : 'keyword',
          detail: item.detail || undefined,
          itemKind: item.kind,
          doc: item.doc || null,
        }));
      }

      const typed = ctx.state.sliceDoc(msg.from, ctx.pos);
      resolve(makeFuzzyResult(msg.from, allOptions, typed));
    };
    worker.postMessage({ type: 'completions', offset: ctx.pos });
  });
}

// ── Persistence ──

const NUM_BUFFERS = 8;
const BUFFER_KEY_PREFIX = 'tau-cam:buffer:';
const ACTIVE_BUFFER_KEY = 'tau-cam:active';

let activeBufferIdx = 0;
const bufferStates = new Array(NUM_BUFFERS).fill(null);

function loadBufferSource(idx) {
  try {
    return localStorage.getItem(BUFFER_KEY_PREFIX + idx);
  } catch (e) { return null; }
}

function saveBufferSource(idx, source) {
  try { localStorage.setItem(BUFFER_KEY_PREFIX + idx, source); } catch (e) {}
}

function saveActiveBufferIdx(idx) {
  try { localStorage.setItem(ACTIVE_BUFFER_KEY, String(idx)); } catch (e) {}
}

function loadActiveBufferIdx() {
  try {
    const v = localStorage.getItem(ACTIVE_BUFFER_KEY);
    const n = v !== null ? parseInt(v, 10) : 0;
    return (n >= 0 && n < NUM_BUFFERS) ? n : 0;
  } catch (e) { return 0; }
}

// Migrate from old single-key storage to per-buffer
function migrateStorage() {
  const OLD_KEY = 'tau-cam:source';
  try {
    const old = localStorage.getItem(OLD_KEY);
    if (old !== null && localStorage.getItem(BUFFER_KEY_PREFIX + '0') === null) {
      localStorage.setItem(BUFFER_KEY_PREFIX + '0', old);
      localStorage.removeItem(OLD_KEY);
    }
  } catch (e) {}
}

// First-run: populate demo buffers if nothing has been saved yet
function seedDemoBuffersIfNew() {
  try {
    let anyBuffer = false;
    for (let i = 0; i < NUM_BUFFERS; i++) {
      if (localStorage.getItem(BUFFER_KEY_PREFIX + i) !== null) { anyBuffer = true; break; }
    }
    if (anyBuffer) return;
    for (const [idx, src] of Object.entries(DEMO_BUFFERS)) {
      saveBufferSource(parseInt(idx, 10), src);
    }
  } catch (e) {}
}

let saveTimer = null;
function saveSource(source) {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(function() {
    saveBufferSource(activeBufferIdx, source);
  }, 500);
}

window.tauReset = function() {
  for (let i = 0; i < NUM_BUFFERS; i++) {
    localStorage.removeItem(BUFFER_KEY_PREFIX + i);
    bufferStates[i] = null;
  }
  localStorage.removeItem(ACTIVE_BUFFER_KEY);
  activeBufferIdx = 0;
  if (view) {
    bufferStates[0] = createBufferState(DEFAULT_SOURCE);
    view.setState(bufferStates[0]);
    if (worker && workerReady) {
      worker.postMessage({ type: 'init', source: DEFAULT_SOURCE });
    }
  }
  updateTabBar();
  console.log('tau-cam reset to default');
};

function saveAllBuffers() {
  // Flush current editor state into cache
  if (view) bufferStates[activeBufferIdx] = view.state;
  for (let i = 0; i < NUM_BUFFERS; i++) {
    if (bufferStates[i] && bufferStates[i].doc.length > 0) {
      saveBufferSource(i, bufferStates[i].doc.toString());
    }
  }
  saveActiveBufferIdx(activeBufferIdx);
}

// ── Default source ──

const HEADER = `--██████╗ ███████╗██████╗ ███████╗███████╗███████╗███████╗██████╗
--██╔══██╗██╔════╝██╔══██╗██╔════╝╚══███╔╝╚══███╔╝██╔════╝██╔══██╗
--██████╔╝█████╗  ██████╔╝█████╗    ███╔╝   ███╔╝ █████╗  ██║  ██║
--██╔══██╗██╔══╝  ██╔══██╗██╔══╝   ███╔╝   ███╔╝  ██╔══╝  ██║  ██║
--██║  ██║███████╗██║  ██║███████╗███████╗███████╗███████╗██████╔╝
--╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝╚══════╝╚══════╝╚══════╝╚══════╝╚═════╝`;

const DEFAULT_SOURCE = HEADER + `
-- Main Riff
@notes = scale(:e1, :minor_pentatonic, num_octaves: 2).shuffle

live_loop :rerezzed do
  use_synth :dsaw
  tick_reset
  t = 0.04
  with_fx :krush, amp: 2 do
    p = play :e3, sustain: 8, release: 0, note_slide: t
    64.times do
      sleep 0.125
      control p, note: @notes.tick
    end
  end
end

live_loop :industry do
  time_warp 0.04 do
    sample :loop_industrial, beat_stretch: 1, amp: 2, rate: 1
  end
  sleep 1
end

live_loop :bd do
  time_warp 0.04 do
    sample :bd_haus, amp: 3, lpf: 100
  end
  sleep 0.5
end`;

const DEMO_BUFFERS = {
  0: DEFAULT_SOURCE,

  1: HEADER + `
--Arp Mode
@notes = scale(:e1, :minor_pentatonic, num_octaves: 2)

live_loop :rerezzed do
  use_synth :dsaw
  tick_reset
  t = 0.04
  with_fx :krush, amp: 2 do
    p = play :e3, sustain: 8, release: 0, note_slide: t
    64.times do
      sleep 0.125
      control p, note: @notes.tick
    end
  end
end

live_loop :industry do
  time_warp 0.04 do
    sample :loop_industrial, beat_stretch: 1, amp: 2, rate: 1
  end
  sleep 1
end

live_loop :bd do
  time_warp 0.04 do
    sample :bd_haus, amp: 3, lpf: 100
  end
  sleep 0.5
end`,

  2: HEADER + `
--Slow Beat
@notes = scale(:e1, :minor_pentatonic, num_octaves: 2).shuffle

live_loop :rerezzed do
  use_synth :dsaw
  tick_reset
  t = 0.04
  with_fx :krush, amp: 2 do
    p = play :e3, sustain: 8, release: 0, note_slide: t
    64.times do
      sleep 0.125
      control p, note: @notes.tick
    end
  end
end

live_loop :industry do
  time_warp 0.04 do
    sample :loop_industrial, beat_stretch: 1, amp: 2, rate: 0.5
  end
  sleep 1
end

live_loop :bd do
  time_warp 0.04 do
    sample :bd_haus, amp: 3, lpf: 100
  end
  sleep 0.5
end`,

  3: HEADER + `
--Rand Riff
use_random_seed 3000333
@notes = scale(:e1, :minor_pentatonic, num_octaves: 4).shuffle

live_loop :rerezzed do
  use_synth :dsaw
  tick_reset
  t = 0.04
  with_fx :krush, amp: 2 do
    p = play :e3, sustain: 8, release: 0, note_slide: t
    64.times do
      sleep 0.125
      control p, note: @notes.tick
    end
  end
end

live_loop :industry do
  time_warp 0.04 do
    sample :loop_industrial, beat_stretch: 1, amp: 2, rate: 1
  end
  sleep 1
end

live_loop :bd do
  time_warp 0.04 do
    sample :bd_haus, amp: 3, lpf: 100
  end
  sleep 0.5
end`,

  4: HEADER + `
--Rand Riff 2
use_random_seed 4000444
@notes = scale(:e2, :minor_pentatonic, num_octaves: 4).shuffle

live_loop :rerezzed do
  use_synth :dsaw
  tick_reset
  t = 0.04
  with_fx :krush, amp: 2 do
    p = play :e3, sustain: 8, release: 0, note_slide: t
    64.times do
      sleep 0.125
      control p, note: @notes.tick
    end
  end
end

live_loop :industry do
  time_warp 0.04 do
    sample :loop_industrial, beat_stretch: 1, amp: 2, rate: 1
  end
  sleep 1
end

live_loop :bd do
  time_warp 0.04 do
    sample :bd_haus, amp: 3, lpf: 100
  end
  sleep 0.5
end`,

  5: HEADER + `
--Arp Mix (active next live loop)
@notes = scale(:e1, :minor_pentatonic, num_octaves: 2).shuffle

live_loop :rerezzed do
  use_synth :dsaw
  tick_reset
  t = 0.04
  with_fx :bitcrusher, amp: 1.5, sample_rate: 13000, cutoff: 110 do
    p = play :e3, sustain: 8, release: 0, note_slide: t
    p2 = play :e3, sustain: 8, release: 0, note_slide: t
    64.times do
      sleep 0.125
      control p, note: @notes.tick
      control p2, note: (octs :e3 3).look
    end
  end
end

live_loop :industry do
  time_warp 0.04 do
    sample :loop_industrial, beat_stretch: 1, amp: 2, rate: 1
  end
  sleep 1
end

live_loop :bd do
  time_warp 0.04 do
    sample :bd_haus, amp: 3, lpf: 100
  end
  sleep 0.5
end`,

  6: HEADER + `
--Riff Only
@notes = scale(:e1, :minor_pentatonic, num_octaves: 2).shuffle

live_loop :rerezzed do
  use_synth :dsaw
  tick_reset
  t = 0.04
  with_fx :krush, amp: 2 do
    p = play :e3, sustain: 8, release: 0, note_slide: t
    64.times do
      sleep 0.125
      control p, note: @notes.tick
    end
  end
end

live_loop :industry do
  time_warp 0.04 do
    -- sample :loop_industrial, beat_stretch: 1, amp: 2, rate: 1
  end
  sleep 1
end

live_loop :bd do
  time_warp 0.04 do
    -- sample :bd_haus, amp: 3, lpf: 100
  end
  sleep 0.5
end`,

  7: HEADER + `
--BOOM!
@notes = scale(:e1, :minor_pentatonic, num_octaves: 2).shuffle

live_loop :rerezzed do
  with_fx :bitcrusher, sample_rate: 13000, bits: 6 do
    sample :misc_cineboom, start: 0.21, amp: 3, rate: 1
  end
  stop
end

live_loop :industry do
  stop
end

live_loop :bd do
  stop
end`,
};

// ── Module state ──

let view = null;
let worker = null;
let errorDebounceTimer = null;
let workerReady = false;
let currentLeaves = [];
let lastResultMsg = null;

// ── Worker setup ──

function initWorker() {
  worker = new Worker('./tau-state/editor_worker.js', { type: 'module' });
  // Send config immediately to trigger WASM init
  worker.postMessage({ type: 'config' });

  worker.onmessage = function (e) {
    const msg = e.data;

    if (msg.type === 'ready') {
      // Sync worker to current editor content (user may have typed during init)
      const source = view ? view.state.doc.toString() : DEFAULT_SOURCE;
      worker.postMessage({ type: 'init', source });
      return;
    }

    if (msg.type === 'result') {
      workerReady = true;
      lastResultMsg = msg;
      currentLeaves = msg.leaves;

      // Update statement map for execution highlighting
      if (msg.statementMap) {
        statementMap.clear();
        for (const entry of msg.statementMap) {
          statementMap.set(entry.id, { start: entry.start, end: entry.end });
        }
        console.log('[tau-cam] statement map:', statementMap.size, 'entries');
      } else {
        console.log('[tau-cam] no statementMap in result');
      }

      // Apply syntax decorations immediately (without error squiggles)
      if (view) {
        const decos = buildDecorations(msg.leaves, msg.errors, view.state.doc, false);
        view.dispatch({ effects: [setDecorations.of(decos)] });
      }

      // Debounce error display — only show after typing pause
      clearTimeout(errorDebounceTimer);
      const pendingErrors = msg.errors;
      const pendingLeaves = msg.leaves;
      if (!pendingErrors || pendingErrors.length === 0) {
        // No errors — clear immediately so fixes feel instant
        if (view) {
          view.dispatch({ effects: [
            setErrorMarkers.of(RangeSet.empty),
            setInlineErrors.of(Decoration.none),
          ] });
        }
        // Clear bottom bar
        const bar = document.getElementById('error-bar');
        if (bar) bar.style.display = 'none';
      } else {
        const showErrors = () => {
          if (!view) return;
          // Don't show errors while completion popup is open
          if (completionStatus(view.state) === 'active') {
            errorDebounceTimer = setTimeout(showErrors, 500);
            return;
          }
          const doc = view.state.doc;
          // Suppress errors on the cursor's current line only if the doc just changed
          // (user may still be typing). If cursor moved without editing, show all errors.
          const cursorLine = doc.lineAt(view.state.selection.main.head).number;
          const visibleErrors = pendingErrors.filter(e => e.line !== cursorLine);
          const decos = buildDecorations(pendingLeaves, visibleErrors, doc, true);
          const gutterMarkers = buildErrorGutterMarkers(visibleErrors, doc);

          // Option 1: Line decoration + inline error widget form one box
          const inlineWidgets = [];
          const seenLines = new Set();
          for (const e of visibleErrors) {
            if (e.line < 1 || e.line > doc.lines || seenLines.has(e.line)) continue;
            seenLines.add(e.line);
            const lineObj = doc.line(e.line);
            // Line background + border top/sides
            inlineWidgets.push(
              Decoration.line({ class: 'cm-error-line' }).range(lineObj.from)
            );
            // Widget with message + border bottom/sides
            inlineWidgets.push(
              Decoration.widget({
                widget: new InlineErrorWidget(e.text),
                block: true,
                side: 1,
              }).range(lineObj.to)
            );
          }
          inlineWidgets.sort((a, b) => a.from - b.from);

          view.dispatch({ effects: [
            setDecorations.of(decos),
            setErrorMarkers.of(gutterMarkers),
            setInlineErrors.of(Decoration.set(inlineWidgets)),
          ] });

          // Bottom bar — bullet list
          let bar = document.getElementById('error-bar');
          if (!bar) {
            bar = document.createElement('div');
            bar.id = 'error-bar';
            bar.style.cssText = 'position:absolute;bottom:32px;left:0;right:0;padding:4px 12px;background:var(--bg-code);border-top:1px solid var(--accent);font-size:11px;color:var(--accent);font-family:inherit;z-index:10;overflow-y:auto;max-height:30%;';
            document.getElementById('editor-mount').style.position = 'relative';
            document.getElementById('editor-mount').appendChild(bar);
          }
          if (visibleErrors.length === 0) {
            bar.style.display = 'none';
          } else {
            bar.innerHTML = visibleErrors
              .map(e => `<div style="padding:1px 0">· line ${e.line}: ${escHtml(e.text)}</div>`)
              .join('');
            bar.style.display = 'block';
          }
        };
        errorDebounceTimer = setTimeout(showErrors, 500);
      }
    }

    if (msg.type === 'nodeInfo') {
      updateNodeInfo(msg);
      if (!completionDocActive && typeof window.updateCtxInfo === 'function') {
        window.updateCtxInfo(msg);
      }
    }

    if (msg.type === 'quickfix') {
      console.log('[quickfix-response] edits:', msg.edits ? msg.edits.length : 'none', msg._debug || '');
      if (msg.edits && msg.edits.length > 0 && view) {
        view.dispatch({ changes: msg.edits.map(e => ({ from: e.from, to: e.to || e.from, insert: e.insert })) });
      }
    }

    if (msg.type === 'formatted') {
      if (view && msg.source !== view.state.doc.toString()) {
        view.dispatch({
          changes: { from: 0, to: view.state.doc.length, insert: msg.source },
        });
      }
      if (formatResolve) { formatResolve(); formatResolve = null; }
    }

    if (msg.type === 'completions') {
      if (completionResolve) {
        const resolve = completionResolve;
        completionResolve = null;
        resolve(msg);
      }
    }
  };
}

// ── Hover + sticky selection for ctx-info ──

let stickyCtxNode = null; // {pos, leaf} — locks ctx-info until cleared

function handleClick(e) {
  if (!view || !worker) return;
  const pos = view.posAtCoords({ x: e.clientX, y: e.clientY });
  if (pos === null || pos < 0) return;
  const leaf = findLeafAt(pos);
  if (leaf) {
    stickyCtxNode = { pos, leaf };
    // Highlight the selected node prominently
    const rgb = hexToRgb(leaf.color);
    const hl = Decoration.mark({
      attributes: {
        style: `background: rgba(${rgb[0]},${rgb[1]},${rgb[2]},0.25); outline: 2px solid rgba(${rgb[0]},${rgb[1]},${rgb[2]},0.6); border-radius: 3px`,
      }
    }).range(leaf.s, leaf.e);
    view.dispatch({ effects: setHighlight.of(Decoration.set([hl])) });
    worker.postMessage({ type: 'nodeAt', offset: pos });
  } else {
    // Clicked on empty space — clear sticky
    stickyCtxNode = null;
    view.dispatch({ effects: setHighlight.of(Decoration.none) });
    const el = document.getElementById('ctx-info');
    if (el) el.innerHTML = '<span class="ctx-hint">hover over code for contextual info</span>';
  }
}

function handleMouseMove(e) {
  if (!view || !worker) return;
  if (completionDocActive) return;
  if (stickyCtxNode) return;
  if (ctxInfoHovered) return;
  const pos = view.posAtCoords({ x: e.clientX, y: e.clientY });
  if (pos === null || pos < 0) return;

  const leaf = findLeafAt(pos);
  if (leaf) {
    const rgb = hexToRgb(leaf.color);
    const hl = Decoration.mark({
      attributes: {
        style: `background: rgba(${rgb[0]},${rgb[1]},${rgb[2]},0.12); outline: 1px solid rgba(${rgb[0]},${rgb[1]},${rgb[2]},0.3); border-radius: 3px`,
      }
    }).range(leaf.s, leaf.e);
    view.dispatch({ effects: setHighlight.of(Decoration.set([hl])) });
  }

  worker.postMessage({ type: 'nodeAt', offset: pos });
}

function handleMouseLeave() {
  if (!view) return;
  if (stickyCtxNode) return;
  view.dispatch({ effects: setHighlight.of(Decoration.none) });
  // Don't clear ctx-info — let it persist so play button etc. remain clickable
}

function findLeafAt(offset) {
  let lo = 0, hi = currentLeaves.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const lf = currentLeaves[mid];
    if (offset < lf.s) hi = mid - 1;
    else if (offset >= lf.e) lo = mid + 1;
    else return lf;
  }
  return null;
}

function updateNodeInfo(msg) {
  const el = document.getElementById('node-info');
  if (!el) return;
  el.innerHTML = '';
}

// ── Re-evaluate error visibility when cursor moves lines ──

let lastErrorCursorLine = -1;

function refreshErrorsOnCursorMove(update) {
  if (!update.selectionSet || !lastResultMsg || !lastResultMsg.errors || lastResultMsg.errors.length === 0) return;
  const doc = update.state.doc;
  const cursorLine = doc.lineAt(update.state.selection.main.head).number;
  if (cursorLine === lastErrorCursorLine) return;
  lastErrorCursorLine = cursorLine;

  if (completionStatus(update.state) === 'active') return;

  const visibleErrors = lastResultMsg.errors;
  const decos = buildDecorations(lastResultMsg.leaves, visibleErrors, doc, true);
  const gutterMarkers = buildErrorGutterMarkers(visibleErrors, doc);

  const inlineWidgets = [];
  const seenLines = new Set();
  for (const e of visibleErrors) {
    if (e.line < 1 || e.line > doc.lines || seenLines.has(e.line)) continue;
    seenLines.add(e.line);
    const lineObj = doc.line(e.line);
    inlineWidgets.push(Decoration.line({ class: 'cm-error-line' }).range(lineObj.from));
    inlineWidgets.push(Decoration.widget({ widget: new InlineErrorWidget(e.text), block: true, side: 1 }).range(lineObj.to));
  }
  inlineWidgets.sort((a, b) => a.from - b.from);

  view.dispatch({ effects: [
    setDecorations.of(decos),
    setErrorMarkers.of(gutterMarkers),
    setInlineErrors.of(Decoration.set(inlineWidgets)),
  ] });
}

// ── Doc changes → worker ──

function sendChanges(update) {
  if (!update.docChanged) return;

  // Remap flash positions through the edit so flashes track correctly.
  // flashMap is the snapshot from the last Run — keep it in sync with edits.
  for (const [id, entry] of flashMap) {
    entry.start = update.changes.mapPos(entry.start, 1);
    entry.end   = update.changes.mapPos(entry.end, -1);
    if (entry.end <= entry.start) flashMap.delete(id);
  }
  for (const [id, flash] of activeFlashes) {
    flash.from = update.changes.mapPos(flash.from, 1);
    flash.to   = update.changes.mapPos(flash.to, -1);
    if (flash.to <= flash.from) {
      clearTimeout(flash.timer);
      activeFlashes.delete(id);
    }
  }

  // Persist to localStorage (debounced)
  saveSource(update.state.doc.toString());

  if (!worker || !workerReady) return;

  const changes = [];
  update.changes.iterChanges((fromA, toA, _fromB, _toB, inserted) => {
    changes.push({ from: fromA, to: toA, insert: inserted.toString() });
  });

  worker.postMessage({ type: 'edit', changes });
}

// ── Completion doc tracking ──

function updateCtxInfoFromCompletion(item) {
  if (window._ctxInfoLocked) return;
  const el = document.getElementById('ctx-info');
  if (!el) return;

  let html = '';
  const kind = item.itemKind;
  const doc = item.doc;

  if (kind === 'synth') {
    html += '<div class="ctx-title"><span class="ctx-tag ctx-tag-use-synth">synth</span>';
    html += '<span style="color:var(--ctx-symbol)">' + escHtml(item.label) + '</span></div>';
    if (doc && doc.desc) {
      html += '<div class="ctx-desc">' + escHtml(doc.desc) + '</div>';
    }
    if (doc && doc.params && doc.params.length > 0) {
      html += '<div class="ctx-section">parameters</div><div class="ctx-params">';
      for (let i = 0; i < doc.params.length; i++) {
        const p = doc.params[i];
        html += '<div class="ctx-param-row">' +
          '<span class="ctx-param-name">' + escHtml(p.name) + '</span>' +
          '<span class="ctx-param-type">' + escHtml(p.type) + '</span>' +
          '<span class="ctx-param-desc">' + escHtml(p.desc) + '</span></div>';
      }
      html += '</div>';
    }
  } else if (kind === 'param') {
    html += '<div class="ctx-title"><span class="ctx-tag ctx-tag-keyword">param</span>';
    html += '<span style="color:var(--ctx-keyword)">' + escHtml(item.label) + '</span></div>';
    if (doc) {
      html += '<div class="ctx-desc">' + escHtml(doc.desc) + '</div>';
      html += '<div style="color:var(--text-dim);margin:2px 0">Type: <span style="color:var(--text-primary)">' + escHtml(doc.type) + '</span></div>';
    }
  } else if (kind === 'statement') {
    html += '<div class="ctx-title"><span class="ctx-tag ctx-tag-play">' + escHtml(item.label) + '</span></div>';
    if (item.detail) {
      html += '<div class="ctx-sig"><span class="ctx-kw">' + escHtml(item.label) + '</span> <span class="ctx-opt">' + escHtml(item.detail) + '</span></div>';
    }
    if (doc && doc.desc) {
      html += '<div class="ctx-desc">' + escHtml(doc.desc) + '</div>';
    }
  } else if (kind === 'fx') {
    html += '<div class="ctx-title"><span class="ctx-tag ctx-tag-sample">fx</span>';
    html += '<span style="color:var(--ctx-symbol)">' + escHtml(item.label) + '</span></div>';
    if (doc && doc.desc) {
      html += '<div class="ctx-desc">' + escHtml(doc.desc) + '</div>';
    }
    if (doc && doc.params && doc.params.length > 0) {
      html += '<div class="ctx-section">parameters</div><div class="ctx-params">';
      for (let i = 0; i < doc.params.length; i++) {
        const p = doc.params[i];
        const type = p.bpm ? 'beats' : (typeof p.default === 'number' ? (Number.isInteger(p.default) ? 'int' : 'float') : 'value');
        html += '<div class="ctx-param-row">' +
          '<span class="ctx-param-name">' + escHtml(p.name) + '</span>' +
          '<span class="ctx-param-type">' + escHtml(String(p.default)) + '</span>' +
          '<span class="ctx-param-desc">' + escHtml(p.doc || '') + '</span></div>';
      }
      html += '</div>';
    }
  } else if (kind === 'sample') {
    const name = item.label.replace(/^:/, '');
    html += renderSampleCtxInfo(name, item.label);
    el.innerHTML = html;
    return;
  } else {
    html += '<div class="ctx-title">' + escHtml(item.label) + '</div>';
    if (item.detail) {
      html += '<div class="ctx-desc">' + escHtml(item.detail) + '</div>';
    }
  }

  el.innerHTML = html;
}

const completionDocListener = EditorView.updateListener.of(update => {
  const sel = selectedCompletion(update.state);
  const label = sel ? sel.label : null;

  if (sel && label !== lastSelectedLabel) {
    lastSelectedLabel = label;
    updateCtxInfoFromCompletion(sel);
    completionDocActive = true;
  } else if (!sel && completionDocActive) {
    const status = completionStatus(update.state);
    if (!status) {
      completionDocActive = false;
      lastSelectedLabel = null;
      if (!ctxInfoHovered && !window._ctxInfoLocked) {
        const el = document.getElementById('ctx-info');
        if (el) el.innerHTML = '<span class="ctx-hint">hover over code for contextual info</span>';
      }
    }
  }
});

// ── TreeNav sample browser ──
// Hierarchical drill-in/out navigation like Helm/Ivy.
// The text inside sample "..." IS the navigation path.
// "/" drills into a folder, backspace past "/" goes up.

const treeNav = {
  panel: null,
  entries: [],      // current level: [{name, isDir, fullPath, shadow, root}]
  selectedIdx: 0,
  active: false,
  lastInput: '',
  _cache: null,     // { roots: [{name, paths}] }
};

function treeNavBuildCache() {
  // Build multi-root tree: built-in samples + each user folder
  const roots = [];

  // Built-in samples (from known sample list in the worker, or from fetch)
  // We'll collect them from sampleMeta or known list
  const builtinPaths = [];
  // Check if we have any built-in sample data
  if (window._builtinSampleNames) {
    for (const n of window._builtinSampleNames) builtinPaths.push(n);
  }
  if (builtinPaths.length > 0) {
    roots.push({ name: 'built-in', paths: builtinPaths.sort() });
  }

  // User folders — each sampleDir is a root, using alias
  const dirs = window._sampleDirs || [];
  const idx = window._userSampleIndex || {};
  for (let di = 0; di < dirs.length; di++) {
    const alias = dirs[di].alias || dirs[di].name || ('folder-' + di);
    const dirPaths = [];
    for (const [p, entry] of Object.entries(idx)) {
      if (entry.dirIdx === di) dirPaths.push(p);
    }
    if (dirPaths.length > 0) {
      roots.push({ name: alias, paths: dirPaths.sort() });
    }
  }

  treeNav._cache = { roots };
}

function treeNavEntriesAt(prefix, filter) {
  if (!treeNav._cache) treeNavBuildCache();
  const cache = treeNav._cache;
  if (!cache) return [];

  const fl = filter.toLowerCase();

  // No prefix: show roots (folder categories)
  if (!prefix) {
    const entries = [];
    for (const root of cache.roots) {
      const isMatch = !fl || root.name.toLowerCase().includes(fl);
      entries.push({
        name: root.name,
        isDir: true,
        fullPath: root.name + '/',
        shadow: !isMatch,
        count: root.paths.length,
      });
    }
    // Sort: matches first, then shadows
    entries.sort((a, b) => (a.shadow === b.shadow) ? a.name.localeCompare(b.name) : a.shadow ? 1 : -1);
    return entries;
  }

  // Find which root(s) match the first segment
  const firstSlash = prefix.indexOf('/');
  const rootName = firstSlash >= 0 ? prefix.substring(0, firstSlash) : prefix;
  const subPrefix = firstSlash >= 0 ? prefix.substring(firstSlash + 1) : '';

  // Collect paths from matching roots
  let allPaths = [];
  for (const root of cache.roots) {
    if (root.name.toLowerCase() === rootName.toLowerCase()) {
      allPaths = allPaths.concat(root.paths);
    }
  }
  if (allPaths.length === 0) {
    // No root match — search ALL paths across all roots
    for (const root of cache.roots) allPaths = allPaths.concat(root.paths);
  }

  const dirs = new Map();
  const files = [];
  const shadowFiles = [];

  for (const p of allPaths) {
    if (subPrefix && !p.startsWith(subPrefix)) continue;
    const rest = subPrefix ? p.substring(subPrefix.length) : p;
    const slashIdx = rest.indexOf('/');
    if (slashIdx >= 0) {
      const dirName = rest.substring(0, slashIdx);
      if (!dirs.has(dirName)) dirs.set(dirName, false);
      if (fl && dirName.toLowerCase().includes(fl)) dirs.set(dirName, true);
      else if (!fl) dirs.set(dirName, true);
    } else {
      const isMatch = !fl || rest.toLowerCase().includes(fl);
      const entry = { name: rest, isDir: false, fullPath: p, shadow: !isMatch };
      if (isMatch) files.push(entry);
      else shadowFiles.push(entry);
    }
  }

  const entries = [];
  for (const [d, matched] of [...dirs].sort((a, b) => a[0].localeCompare(b[0]))) {
    if (matched) entries.push({ name: d, isDir: true, fullPath: prefix + d + '/', shadow: false });
  }
  for (const [d, matched] of [...dirs].sort((a, b) => a[0].localeCompare(b[0]))) {
    if (!matched) entries.push({ name: d, isDir: true, fullPath: prefix + d + '/', shadow: true });
  }
  for (const f of files.sort((a, b) => a.name.localeCompare(b.name))) entries.push(f);
  if (fl && files.length < 10) {
    for (const f of shadowFiles.sort((a, b) => a.name.localeCompare(b.name)).slice(0, 10)) entries.push(f);
  }
  return entries;
}

function treeNavHighlight(text, filter) {
  if (!filter) return escHtml(text);
  const re = new RegExp('(' + filter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'gi');
  return escHtml(text).replace(re, '<span class="treenav-match">$1</span>');
}

function treeNavRender() {
  const panel = treeNav.panel;
  if (!panel) return;

  // Position below cursor
  if (view) {
    const pos = view.state.selection.main.head;
    const coords = view.coordsAtPos(pos);
    if (coords) {
      const editorRect = view.dom.getBoundingClientRect();
      panel.style.left = Math.max(0, coords.left - editorRect.left) + 'px';
      panel.style.top = (coords.bottom - editorRect.top + 2) + 'px';
    }
  }

  const max = 30;
  const show = treeNav.entries.slice(0, max);
  const filter = treeNav._filter || '';
  const prefix = treeNav._prefix || '';
  const primaryCount = treeNav.entries.filter(e => !e.shadow).length;

  let html = '<div class="treenav-status">';
  if (prefix) html += '<span>' + escHtml(prefix) + '</span> ';
  html += primaryCount + ' match' + (primaryCount !== 1 ? 'es' : '');
  html += '</div>';

  // For root entries, show sample count
  const isRootLevel = !prefix && !filter;

  for (let i = 0; i < show.length; i++) {
    const e = show[i];
    const sel = i === treeNav.selectedIdx ? ' selected' : '';
    const shadow = e.shadow ? ' shadow' : '';
    html += '<div class="treenav-row' + sel + shadow + '" data-idx="' + i + '">';
    if (e.isDir) {
      html += '<span class="treenav-dir">' + treeNavHighlight(e.name, filter) + '/</span>';
      if (e.count) html += '<span style="color:#888;margin-left:8px;font-size:11px">' + e.count + '</span>';
    } else {
      html += '<span class="treenav-file">' + treeNavHighlight(e.name, filter) + '</span>';
    }
    html += '</div>';
  }
  if (treeNav.entries.length > max) {
    html += '<div class="treenav-status">... ' + (treeNav.entries.length - max) + ' more</div>';
  }

  panel.innerHTML = html;
  const selEl = panel.querySelector('.treenav-row.selected');
  if (selEl) selEl.scrollIntoView({ block: 'nearest' });

  treeNavUpdateCtx();
}

function treeNavUpdateCtx() {
  const el = document.getElementById('ctx-info');
  if (!el || treeNav.entries.length === 0) return;
  const entry = treeNav.entries[treeNav.selectedIdx];
  if (!entry) return;

  // Trigger on-demand analysis for files
  if (!entry.isDir && window._analyzeSampleOnDemand) {
    const meta = window._sampleMeta && window._sampleMeta[entry.fullPath];
    if (!meta) {
      window._analyzeSampleOnDemand(entry.fullPath).then(() => {
        // Re-render ctx when analysis completes
        if (treeNav.active && treeNav.entries[treeNav.selectedIdx] === entry) {
          treeNavUpdateCtx();
        }
      });
    }
  }

  let html = '<div class="ctx-title"><span class="ctx-tag ctx-tag-sample">' +
    (entry.isDir ? 'folder' : 'sample') + '</span>';
  html += '<span style="color:#fff">' + escHtml(entry.name) + '</span></div>';

  if (entry.isDir) {
    const prefix = entry.fullPath;
    const allInside = treeNav.allPaths ? treeNav.allPaths.filter(p => p.startsWith(prefix)) : [];
    const count = allInside.length;
    // Count subfolders
    const subDirs = new Set();
    for (const p of allInside) {
      const rest = p.substring(prefix.length);
      const slash = rest.indexOf('/');
      if (slash >= 0) subDirs.add(rest.substring(0, slash));
    }
    html += '<div style="color:#ccc;font-size:13px;margin:6px 0">' + count + ' sample' + (count !== 1 ? 's' : '') + '</div>';
    if (subDirs.size > 0) {
      html += '<div style="color:#aaa;font-size:12px;margin:4px 0">' + subDirs.size + ' subfolder' + (subDirs.size !== 1 ? 's' : '') + '</div>';
      // Show first few subfolder names
      const showDirs = [...subDirs].sort();
      html += '<div style="margin:6px 0;font-size:12px;line-height:1.6">';
      for (const d of showDirs) {
        html += '<div style="color:#bbb">' + escHtml(d) + '/</div>';
      }
      html += '</div>';
    } else {
      // Show first few filenames
      const showFiles = allInside.map(p => p.substring(prefix.length));
      html += '<div style="margin:6px 0;font-size:12px;line-height:1.6">';
      for (const f of showFiles) {
        html += '<div style="color:#bbb">' + escHtml(f) + '</div>';
      }
      html += '</div>';
    }
    html += '<div style="color:#e060c0;font-size:12px;margin-top:8px">Tab → enter folder</div>';
  } else {
    html += '<div style="color:#bbb;font-size:12px;margin:6px 0">' + escHtml(entry.fullPath) + '</div>';
    // Show sample metadata + waveform if available
    const meta = window._sampleMeta && window._sampleMeta[entry.fullPath];
    if (meta) {
      html += '<div style="color:#ccc;font-size:12px;margin:6px 0;line-height:1.6">';
      html += '<div>' + meta.duration.toFixed(3) + 's &nbsp; ' + meta.sampleRate + 'Hz &nbsp; ' + meta.numChannels + 'ch</div>';
      html += '<div>' + meta.numFrames + ' frames &nbsp; ' + meta.onsets.length + ' onsets</div>';
      html += '</div>';
      // Render waveform as inline SVG
      if (meta.waveform) {
        const w = 220, h = 60;
        const bins = meta.waveform.mins.length;
        let path = '';
        // Draw min/max envelope
        for (let i = 0; i < bins; i++) {
          const x = (i / bins) * w;
          const yMin = h / 2 - meta.waveform.mins[i] * (h / 2);
          const yMax = h / 2 - meta.waveform.maxs[i] * (h / 2);
          path += (i === 0 ? 'M' : 'L') + x.toFixed(1) + ' ' + yMax.toFixed(1) + ' ';
        }
        for (let i = bins - 1; i >= 0; i--) {
          const x = (i / bins) * w;
          const yMin = h / 2 - meta.waveform.mins[i] * (h / 2);
          path += 'L' + x.toFixed(1) + ' ' + yMin.toFixed(1) + ' ';
        }
        path += 'Z';
        html += '<svg width="' + w + '" height="' + h + '" style="margin:4px 0;display:block">';
        html += '<rect width="' + w + '" height="' + h + '" fill="#111" rx="3"/>';
        html += '<path d="' + path + '" fill="rgba(200,80,180,0.5)" stroke="rgba(200,80,180,0.8)" stroke-width="0.5"/>';
        // Onset markers
        for (let i = 0; i < meta.onsets.length; i++) {
          const x = (meta.onsets[i] * w).toFixed(1);
          html += '<line x1="' + x + '" y1="0" x2="' + x + '" y2="' + h + '" stroke="rgba(255,255,255,0.25)" stroke-width="0.5"/>';
        }
        html += '</svg>';
      }
    }
    html += '<div style="color:#e060c0;font-size:12px;margin-top:6px">Tab → select sample</div>';
  }
  el.innerHTML = html;
}

function treeNavShow() {
  if (!treeNav.panel) {
    treeNav.panel = document.getElementById('treeNav-panel');
    if (treeNav.panel) {
      treeNav.panel.addEventListener('click', (e) => {
        const row = e.target.closest('.treenav-row');
        if (row) {
          treeNav.selectedIdx = parseInt(row.dataset.idx) || 0;
          treeNavSelect();
        }
      });
    }
  }
  if (treeNav.panel) treeNav.panel.classList.add('visible');
  treeNav.active = true;
}

function treeNavHide() {
  if (treeNav.panel) treeNav.panel.classList.remove('visible');
  treeNav.active = false;
  treeNav.lastInput = '';
}

function treeNavParseInput(lineText) {
  // Extract the content inside the first "..." on the sample line
  const m = lineText.match(/sample\s+"([^"]*)(")?/);
  if (!m) return null;
  return m[1]; // the text inside quotes (possibly incomplete — no closing quote yet)
}

function treeNavUpdate(state) {
  // Invalidate cache if sample dirs changed
  const dirCount = (window._sampleDirs || []).length;
  if (treeNav._lastDirCount !== dirCount) {
    treeNav._lastDirCount = dirCount;
    treeNav._cache = null;
  }

  const idx = window._userSampleIndex;
  const hasBuiltin = window._builtinSampleNames && window._builtinSampleNames.length > 0;
  if ((!idx || Object.keys(idx).length === 0) && !hasBuiltin) { treeNavHide(); return; }

  const pos = state.selection.main.head;
  const line = state.doc.lineAt(pos);
  const text = line.text.trimStart();

  if (!text.startsWith('sample ')) { treeNavHide(); return; }

  const input = treeNavParseInput(text);
  if (input === null) { treeNavHide(); return; }

  if (input === treeNav.lastInput) return; // no change
  treeNav.lastInput = input;

  // Split input into prefix (path up to last /) and filter (remainder)
  const lastSlash = input.lastIndexOf('/');
  const prefix = lastSlash >= 0 ? input.substring(0, lastSlash + 1) : '';
  const filter = lastSlash >= 0 ? input.substring(lastSlash + 1) : input;

  treeNav._prefix = prefix;
  treeNav._filter = filter;
  treeNav.entries = treeNavEntriesAt(prefix, filter);
  treeNav.selectedIdx = 0;

  treeNavShow();
  treeNavRender();
}

function treeNavSelect() {
  // Tab on a folder → drill in (append name/ to the input)
  // Tab on a file → insert the full path
  if (!treeNav.active || treeNav.entries.length === 0 || !view) return false;
  const entry = treeNav.entries[treeNav.selectedIdx];
  if (!entry) return false;

  const pos = view.state.selection.main.head;
  const line = view.state.doc.lineAt(pos);
  const text = line.text;
  const indent = text.match(/^(\s*)/)[1];

  if (entry.isDir) {
    // Drill in: update the "..." content to include this folder
    const quoteStart = text.indexOf('"');
    if (quoteStart < 0) return false;
    // Find end of current input (next " or end of line)
    let quoteEnd = text.indexOf('"', quoteStart + 1);
    if (quoteEnd < 0) quoteEnd = text.length;
    const newInput = entry.fullPath;
    view.dispatch({
      changes: { from: line.from + quoteStart + 1, to: line.from + quoteEnd, insert: newInput },
      selection: { anchor: line.from + quoteStart + 1 + newInput.length },
    });
    return true;
  } else {
    // Select file: replace entire sample statement
    const kwargMatch = text.match(/",?\s*\w+:/);
    const afterQuote = kwargMatch ? text.substring(text.indexOf('"', text.indexOf('"') + 1) + 1) : '';
    const kwargs = afterQuote.match(/^,?\s*(\w+:.*)/) ? afterQuote : '';
    const newText = indent + 'sample "' + entry.fullPath + '"' + kwargs;
    view.dispatch({
      changes: { from: line.from, to: line.to, insert: newText },
    });
    treeNavHide();
    return true;
  }
}

const sampleFilterListener = EditorView.updateListener.of(update => {
  if (update.selectionSet || update.docChanged) {
    treeNavUpdate(update.state);
    // Always show sample info when on a sample line (even without TreeNav)
    if (!treeNav.active) {
      updateSampleLineInfo(update.state);
    }
  }
});

function updateSampleLineInfo(state) {
  if (completionDocActive || window._ctxInfoLocked) return;
  const pos = state.selection.main.head;
  const line = state.doc.lineAt(pos);
  const text = line.text.trimStart();
  if (!text.startsWith('sample ')) return;

  const symMatch = text.match(/^sample\s+:(\w+)/);
  const strMatch = text.match(/^sample\s+"([^"]+)"/);
  const name = symMatch ? symMatch[1] : strMatch ? strMatch[1] : null;
  if (!name) return;

  const el = document.getElementById('ctx-info');
  if (!el) return;
  el.innerHTML = renderSampleCtxInfo(name, symMatch ? ':' + name : '"' + name + '"');
}

// ── Sample ctx-info renderer (waveform, spectrogram, stats, play button) ──

function renderSampleCtxInfo(name, label) {
  let html = '<div style="color:#fff;font-size:18px;font-weight:bold;margin-bottom:6px">' + escHtml(label || ':' + name) + '</div>';

  const meta = window._sampleMeta && window._sampleMeta[name];
  if (!meta) {
    html += '<div style="color:#888;font-size:12px;margin:6px 0">analysing...</div>';
    if (window._analyzeSampleOnDemand) {
      window._analyzeSampleOnDemand(name).then(() => {
        if (lastSelectedLabel === (label || ':' + name)) updateCtxInfoFromCompletion({ label: label || ':' + name, itemKind: 'sample' });
      });
    }
    return html;
  }

  html += '<div style="display:flex;align-items:center;gap:10px;margin:4px 0">';
  html += '<button class="ctx-play-btn" data-sample="' + escHtml(name) + '">&#9654; play</button>';
  html += '<span style="color:#bbb;font-size:12px">' + meta.duration.toFixed(3) + 's &nbsp; ' + (meta.sampleRate/1000).toFixed(1) + 'kHz &nbsp; ' + meta.numChannels + 'ch &nbsp; ' + meta.onsets.length + ' onsets</span>';
  html += '</div>';

  const w = 400, h = 110;

  // Waveform — gradient fill with hot highlights at peaks
  if (meta.waveform) {
    const bins = meta.waveform.mins.length;
    let pathTop = '', pathBot = '';
    for (let i = 0; i < bins; i++) {
      const x = (i / bins) * w;
      pathTop += (i === 0 ? 'M' : 'L') + x.toFixed(1) + ' ' + (h/2 - meta.waveform.maxs[i] * h/2).toFixed(1) + ' ';
    }
    for (let i = bins - 1; i >= 0; i--) {
      pathBot += 'L' + ((i / bins) * w).toFixed(1) + ' ' + (h/2 - meta.waveform.mins[i] * h/2).toFixed(1) + ' ';
    }
    const path = pathTop + pathBot + 'Z';
    html += '<div style="margin:3px 0">';
    html += '<svg viewBox="0 0 ' + w + ' ' + h + '" style="display:block;width:100%;max-width:400px;height:auto">';
    html += '<defs>';
    // Silver/glass fill
    html += '<linearGradient id="wg" x1="0" y1="0" x2="0" y2="1">';
    html += '<stop offset="0%" stop-color="#fff" stop-opacity="0.6"/>';
    html += '<stop offset="40%" stop-color="#b0c0d0" stop-opacity="0.2"/>';
    html += '<stop offset="50%" stop-color="#6080a0" stop-opacity="0.1"/>';
    html += '<stop offset="60%" stop-color="#b0c0d0" stop-opacity="0.2"/>';
    html += '<stop offset="100%" stop-color="#fff" stop-opacity="0.6"/>';
    html += '</linearGradient>';
    // Rainbow prism stroke
    html += '<linearGradient id="prism" x1="0" y1="0" x2="1" y2="0">';
    html += '<stop offset="0%" stop-color="#ff0050"/><stop offset="14%" stop-color="#ff8000"/>';
    html += '<stop offset="28%" stop-color="#ffe000"/><stop offset="42%" stop-color="#00ff80"/>';
    html += '<stop offset="57%" stop-color="#00aaff"/><stop offset="71%" stop-color="#8000ff"/>';
    html += '<stop offset="85%" stop-color="#ff0080"/><stop offset="100%" stop-color="#ff0050"/>';
    html += '</linearGradient>';
    html += '<filter id="glow"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>';
    html += '</defs>';
    html += '<rect width="' + w + '" height="' + h + '" fill="#06060a" rx="4"/>';
    // Prismatic dispersion — spectral copies offset like light through glass
    const spectral = [
      { color: 'rgba(255,0,80,0.2)', dx: -2.5, dy: -1 },
      { color: 'rgba(255,140,0,0.18)', dx: -1.5, dy: -0.7 },
      { color: 'rgba(255,240,0,0.15)', dx: -0.5, dy: -0.3 },
      { color: 'rgba(0,255,120,0.15)', dx: 0.5, dy: 0.3 },
      { color: 'rgba(0,160,255,0.12)', dx: 1.5, dy: 0.7 },
      { color: 'rgba(130,0,255,0.14)', dx: 2.5, dy: 1 },
    ];
    for (const s of spectral) {
      html += '<path d="' + path + '" fill="' + s.color + '" stroke="' + s.color.replace(/[\d.]+\)$/, '0.4)') + '" stroke-width="1" transform="translate(' + s.dx + ',' + s.dy + ')" filter="url(#glow)"/>';
    }
    // Bright white glass core
    html += '<path d="' + path + '" fill="url(#wg)" stroke="rgba(255,255,255,0.7)" stroke-width="0.8"/>';
    // Animated prism highlight flowing along contour
    html += '<path d="' + path + '" fill="none" stroke="url(#prism)" stroke-width="1" opacity="0.5" class="prism-stroke"/>';
    // Onset markers
    for (let i = 0; i < meta.onsets.length; i++) {
      const x = (meta.onsets[i] * w).toFixed(1);
      html += '<line x1="' + x + '" y1="0" x2="' + x + '" y2="' + h + '" stroke="rgba(255,255,255,0.3)" stroke-width="0.5"/>';
    }
    html += '</svg></div>';
  }

  // Spectrogram — rainbow chromatic with lens overlay
  if (meta.spectrum && meta.spectrum.spectrogram) {
    const sp = meta.spectrum;
    const sh = 90;
    html += '<div style="margin:3px 0">';
    html += '<svg viewBox="0 0 ' + w + ' ' + sh + '" style="display:block;width:100%;max-width:400px;height:auto">';
    html += '<defs><filter id="sglow"><feGaussianBlur stdDeviation="1.2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>';
    html += '<rect width="' + w + '" height="' + sh + '" fill="#06060a" rx="4"/>';
    const cw = w / sp.specCols;
    const ch = sh / sp.specRows;
    // Main spectrogram — white/silver glass with brightness from intensity
    for (let r = 0; r < sp.specRows; r++) {
      for (let c = 0; c < sp.specCols; c++) {
        const v = sp.spectrogram[r][c];
        if (v < 0.015) continue;
        const t = Math.pow(v, 0.35);
        const bright = Math.floor(150 + t * 105);
        const x = (c * cw).toFixed(1);
        const y = (sh - (r + 1) * ch).toFixed(1);
        html += '<rect x="' + x + '" y="' + y + '" width="' + (cw + 0.5).toFixed(1) + '" height="' + (ch + 0.5).toFixed(1) + '" fill="rgba(' + bright + ',' + bright + ',' + (bright + 10) + ',' + (t * 0.7).toFixed(2) + ')"/>';
      }
    }
    // Spectral dispersion layers
    const specGhosts = [
      { color: [255,0,80], dx: -1.5, dy: -0.8 },
      { color: [255,200,0], dx: -0.5, dy: -0.3 },
      { color: [0,255,120], dx: 0.5, dy: 0.3 },
      { color: [80,0,255], dx: 1.5, dy: 0.8 },
    ];
    for (const g of specGhosts) {
      for (let r = 0; r < sp.specRows; r++) {
        for (let c = 0; c < sp.specCols; c++) {
          const v = sp.spectrogram[r][c];
          if (v < 0.08) continue;
          const t = Math.pow(v, 0.4);
          const x = (c * cw + g.dx).toFixed(1);
          const y = (sh - (r + 1) * ch + g.dy).toFixed(1);
          html += '<rect x="' + x + '" y="' + y + '" width="' + (cw + 0.5).toFixed(1) + '" height="' + (ch + 0.5).toFixed(1) + '" fill="rgba(' + g.color.join(',') + ',' + (t * 0.15).toFixed(2) + ')"/>';
        }
      }
    }
    html += '</svg></div>';
  }

  // Frequency bands — glass prism style
  if (meta.spectrum && meta.spectrum.bands) {
    const bands = meta.spectrum.bands;
    const bh = 50;
    let maxBand = 0;
    for (let i = 0; i < bands.length; i++) if (bands[i] > maxBand) maxBand = bands[i];
    if (maxBand > 0) {
      html += '<div style="margin:3px 0">';
      html += '<svg viewBox="0 0 ' + w + ' ' + bh + '" style="display:block;width:100%;max-width:400px;height:auto">';
      html += '<defs><filter id="bglow"><feGaussianBlur stdDeviation="1.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>';
      html += '<rect width="' + w + '" height="' + bh + '" fill="#06060a" rx="4"/>';
      const bw = w / bands.length;
      // Spectral ghost bars
      const barGhosts = [
        { color: '255,40,80', dx: -1.2 },
        { color: '255,200,0', dx: -0.4 },
        { color: '0,200,120', dx: 0.4 },
        { color: '80,40,255', dx: 1.2 },
      ];
      for (const g of barGhosts) {
        for (let i = 0; i < bands.length; i++) {
          const v = Math.pow(bands[i] / maxBand, 0.45);
          if (v < 0.1) continue;
          const barH = v * (bh - 2);
          html += '<rect x="' + (i * bw + 0.5 + g.dx).toFixed(1) + '" y="' + (bh - 1 - barH).toFixed(1) + '" width="' + Math.max(1, bw - 1).toFixed(1) + '" height="' + barH.toFixed(1) + '" fill="rgba(' + g.color + ',' + (v * 0.15).toFixed(2) + ')" rx="0.5"/>';
        }
      }
      // White glass bars
      for (let i = 0; i < bands.length; i++) {
        const v = Math.pow(bands[i] / maxBand, 0.45);
        const barH = v * (bh - 2);
        const bright = Math.floor(180 + v * 75);
        html += '<rect x="' + (i * bw + 0.5).toFixed(1) + '" y="' + (bh - 1 - barH).toFixed(1) + '" width="' + Math.max(1, bw - 1).toFixed(1) + '" height="' + barH.toFixed(1) + '" fill="rgba(' + bright + ',' + bright + ',' + (bright + 10) + ',' + (0.3 + v * 0.5).toFixed(2) + ')" rx="0.5" filter="url(#bglow)"/>';
      }
      html += '</svg></div>';
    }
  }

  // Stats
  if (meta.spectrum) {
    const sp = meta.spectrum;
    html += '<div style="color:#999;font-size:11px;margin:4px 0;line-height:1.6">';
    html += '<span style="color:#ff6090">centroid</span> ' + sp.spectralCentroid.toFixed(0) + 'Hz &nbsp; ';
    html += '<span style="color:#60a0ff">peak</span> ' + sp.peakFreq.toFixed(0) + 'Hz &nbsp; ';
    html += '<span style="color:#ffc040">RMS</span> ' + sp.rms.toFixed(3) + ' &nbsp; ';
    html += '<span style="color:#40e0c0">ZCR</span> ' + sp.zeroCrossingRate.toFixed(0) + '/s';
    html += '</div>';
  }

  return html;
}

// ── ctx-info interaction: play button + hover lock ──
let ctxInfoHovered = false;
{
  const ctxEl = document.getElementById('ctx-info');
  if (ctxEl) {
    ctxEl.addEventListener('mouseenter', () => { ctxInfoHovered = true; });
    ctxEl.addEventListener('mouseleave', () => { ctxInfoHovered = false; });
    ctxEl.addEventListener('mousedown', (e) => {
      // Prevent all mousedowns in ctx-info from stealing editor focus
      e.preventDefault();
      const btn = e.target.closest('.ctx-play-btn');
      if (btn) {
        const name = btn.dataset.sample;
        if (name && window._previewSample) window._previewSample(name);
      }
    });
  }
}

// ── Emacs kill ring ──
let _killRing = '';

// ── Toggle comment ──

function toggleComment(v) {
  const state = v.state;
  const sel = state.selection.main;
  const fromLine = state.doc.lineAt(sel.from);
  const toLine = state.doc.lineAt(sel.to);
  let allCommented = true;
  for (let i = fromLine.number; i <= toLine.number; i++) {
    const line = state.doc.line(i);
    if (line.text.trim() !== '' && !line.text.match(/^--/)) {
      allCommented = false;
      break;
    }
  }
  const changes = [];
  for (let i = fromLine.number; i <= toLine.number; i++) {
    const line = state.doc.line(i);
    if (allCommented) {
      // Uncomment: remove "-- " or "--" from column 0
      const m = line.text.match(/^--(?: )?/);
      if (m) changes.push({ from: line.from, to: line.from + m[0].length });
    } else {
      if (line.text.trim() !== '') {
        changes.push({ from: line.from, insert: '-- ' });
      }
    }
  }
  if (changes.length > 0) v.dispatch({ changes });
  return true;
}

// ── Block-cursor char decoration ──
//
// Puts a `.cm-cursor-char` class on the character at the caret so the
// "block" cursor style can paint a green bg + black fg on the letter
// itself (real terminal behaviour), instead of an opaque overlay div
// hiding it. Only active when body.cursor-block is set. At end-of-line
// or with a non-empty selection, no mark is emitted and drawSelection's
// overlay handles the caret as usual.

const blockCursorCharPlugin = ViewPlugin.fromClass(class {
  constructor(view) { this.decorations = this.build(view); }
  update(u) {
    if (u.selectionSet || u.docChanged || u.viewportChanged) {
      this.decorations = this.build(u.view);
    }
  }
  build(view) {
    if (!document.body.classList.contains('cursor-block')) return Decoration.none;
    const sel = view.state.selection.main;
    if (!sel.empty) return Decoration.none;
    const pos = sel.head;
    const line = view.state.doc.lineAt(pos);
    if (pos >= line.to) return Decoration.none;
    return Decoration.set([
      Decoration.mark({ class: 'cm-cursor-char' }).range(pos, pos + 1),
    ]);
  }
}, { decorations: v => v.decorations });

// Rebuild decorations when the cursor-style body class changes.
function refreshBlockCursor() {
  if (view) view.dispatch({ selection: view.state.selection });
}

// ── Create editor ──

const readOnlyCompartment = new Compartment();

function makeExtensions() {
  return [
    keymap.of([
      { key: 'Mod-Enter', run: () => {
        const btn = document.getElementById('btn-run');
        if (btn && !btn.disabled) btn.click();
        return true;
      }},
      { key: 'Ctrl-k', run: (v) => {
        const pos = v.state.selection.main.head;
        const line = v.state.doc.lineAt(pos);
        const toEnd = line.to;
        if (pos === toEnd) {
          if (toEnd < v.state.doc.length) {
            _killRing = '\n';
            v.dispatch({ changes: { from: pos, to: pos + 1 } });
          }
        } else {
          _killRing = v.state.sliceDoc(pos, toEnd);
          v.dispatch({ changes: { from: pos, to: toEnd } });
        }
        return true;
      }},
      { key: 'Ctrl-y', run: (v) => {
        if (_killRing) {
          v.dispatch(v.state.replaceSelection(_killRing));
        }
        return true;
      }},
      { key: 'Tab', run: (v) => {
        if (treeNav.active && treeNav.entries.length > 0) { treeNavSelect(); return true; }
        return acceptCompletion(v) || indentMore(v);
      }},
      { key: 'ArrowUp', run: () => {
        if (treeNav.active && treeNav.entries.length > 0) {
          treeNav.selectedIdx = (treeNav.selectedIdx - 1 + treeNav.entries.length) % treeNav.entries.length;
          treeNavRender(); return true;
        }
        return false;
      }},
      { key: 'ArrowDown', run: () => {
        if (treeNav.active && treeNav.entries.length > 0) {
          treeNav.selectedIdx = (treeNav.selectedIdx + 1) % treeNav.entries.length;
          treeNavRender(); return true;
        }
        return false;
      }},
      { key: 'Ctrl-p', run: () => {
        if (treeNav.active && treeNav.entries.length > 0) {
          treeNav.selectedIdx = (treeNav.selectedIdx - 1 + treeNav.entries.length) % treeNav.entries.length;
          treeNavRender(); return true;
        }
        return false;
      }},
      { key: 'Ctrl-n', run: () => {
        if (treeNav.active && treeNav.entries.length > 0) {
          treeNav.selectedIdx = (treeNav.selectedIdx + 1) % treeNav.entries.length;
          treeNavRender(); return true;
        }
        return false;
      }},
      { key: 'Escape', run: () => {
        if (treeNav.active) { treeNavHide(); return true; }
        if (stickyCtxNode) {
          stickyCtxNode = null;
          if (view) view.dispatch({ effects: setHighlight.of(Decoration.none) });
          const el = document.getElementById('ctx-info');
          if (el) el.innerHTML = '<span class="ctx-hint">hover over code for contextual info</span>';
          return true;
        }
        return false;
      }},
      { key: 'Mod-/', run: toggleComment },
      { key: 'Alt-/', run: toggleComment },
      { key: 'Shift-Tab', run: indentLess },
      { key: 'Enter', run: (view) => {
        const pos = view.state.selection.main.head;
        const line = view.state.doc.lineAt(pos);
        const text = line.text.trimEnd();
        const indent = line.text.match(/^\s*/)[0];

        if (/\bdo\s*$/.test(text)) {
          const lineNum = line.number;
          const needsEnd = lastResultMsg && lastResultMsg.errors &&
            lastResultMsg.errors.some(e => e.line <= lineNum && (e.endLine || e.line) >= lineNum);
          if (needsEnd) {
            view.dispatch({
              changes: { from: pos, insert: '\n' + indent + '  \n' + indent + 'end' },
              selection: { anchor: pos + 1 + indent.length + 2 },
            });
            return true;
          }
        }

        let depth = 0;
        for (let i = 1; i <= line.number; i++) {
          const l = view.state.doc.line(i).text.trim();
          if (/\bdo\s*$/.test(l) || /\bdo\s*\|[^|]*\|\s*$/.test(l) || /^if\b/.test(l)) depth++;
          if (/^end\s*$/.test(l) || /^else\s*$/.test(l)) depth = Math.max(0, depth - 1);
          if (/^else\s*$/.test(l)) depth++;
        }
        const newIndent = '  '.repeat(depth);
        view.dispatch({
          changes: { from: pos, insert: '\n' + newIndent },
          selection: { anchor: pos + 1 + newIndent.length },
        });
        return true;
      }},
      // Buffer switching: Alt+0..N
      ...Array.from({ length: NUM_BUFFERS }, (_, i) => ({
        key: `Alt-${i}`,
        run: () => { switchBuffer(i); return true; },
      })),
      ...historyKeymap,
      ...defaultKeymap,
    ]),
    history(),
    drawSelection(),
    blockCursorCharPlugin,
    lineNumbers(),
    tabSize,
    darkTheme,
    errorMarkerField,
    errorGutter,
    inlineErrorField,
    liveLoopBarPlugin,
    decorationField,
    highlightField,
    readOnlyCompartment.of(EditorState.readOnly.of(false)),
    autocompletion({
      override: [tauCompletion],
      defaultKeymap: true,
      activateOnTyping: true,
      icons: false,
    }),
    EditorView.updateListener.of(sendChanges),
    EditorView.updateListener.of(refreshErrorsOnCursorMove),
    EditorView.updateListener.of(u => { if (u.docChanged) window._onSourceDirty?.(); }),
    completionDocListener,
    sampleFilterListener,
    EditorView.inputHandler.of((v, from, _to, text) => {
      if (text === ' ') {
        const sel = selectedCompletion(v.state);
        // Only sample items get the SPC-to-preview shortcut. Previously this
        // also caught any `:`-prefixed item (e.g. synth names like :dull_bell),
        // which sent `sample :<synth>` through _runSnippet and triggered a boot
        // for a snippet that can never play.
        if (sel && sel.type === 'sample') {
          const status = completionStatus(v.state);
          if (status === 'active') {
            const name = sel.label.replace(/^:/, '');
            if (window._previewSample) window._previewSample(name);
            return true;
          }
        }
        const line = v.state.doc.lineAt(from);
        const before = line.text.slice(0, from - line.from);
        if (/(?:^|\s)(use_synth|synth|sample|play|use_fx|sync|get|set|cue)$/.test(before)) {
          setTimeout(() => startCompletion(v), 0);
        }
        console.log('[quickfix-trigger] from:', from, 'worker:', !!worker, 'ready:', workerReady);
        if (worker && workerReady && from > 0) {
          setTimeout(() => worker.postMessage({ type: 'quickfix', offset: from - 1 }), 50);
        }
      }
      return false;
    }),
    EditorView.domEventHandlers({
      mousemove: handleMouseMove,
      mouseleave: handleMouseLeave,
      click: handleClick,
    }),
  ];
}

function createBufferState(doc) {
  return EditorState.create({ doc, extensions: makeExtensions() });
}

// ── Buffer switching ──

function switchBuffer(idx) {
  if (idx < 0 || idx >= NUM_BUFFERS) return;
  if (idx === activeBufferIdx && bufferStates[idx]) return;

  const mount = document.getElementById('editor-mount');

  // Cache current state
  if (view) {
    bufferStates[activeBufferIdx] = view.state;
  }

  // Load or create target buffer
  if (!bufferStates[idx]) {
    const saved = loadBufferSource(idx);
    const doc = saved !== null ? saved : (idx === 0 ? DEFAULT_SOURCE : '');
    bufferStates[idx] = createBufferState(doc);
  }

  function finishSwitch() {
    activeBufferIdx = idx;
    view.setState(bufferStates[idx]);
    saveActiveBufferIdx(idx);

    // Re-sync worker with new buffer content
    if (worker && workerReady) {
      worker.postMessage({ type: 'init', source: view.state.doc.toString() });
    }

    // Clear per-buffer runtime state
    clearTimeout(errorDebounceTimer);
    flashMap.clear();
    for (const [, f] of activeFlashes) clearTimeout(f.timer);
    activeFlashes.clear();
    statementMap.clear();
    lastResultMsg = null;
    stickyCtxNode = null;

    // Clear error display
    const bar = document.getElementById('error-bar');
    if (bar) bar.style.display = 'none';

    updateTabBar();
    // Only focus editor on desktop — on mobile it triggers the virtual keyboard
    if (!window.matchMedia('(pointer: coarse)').matches) view.focus();
    // Jam-mode tab-switch behaviour
    if (window._jamMode) {
      const mode = window._jamSwitchMode;
      if (mode === 'run' && window._triggerLayerRun) {
        // 'run': layer a new VM onto the editor session without stopping
        // the previous one — tap-to-loop / tap-to-stack semantics.
        window._triggerLayerRun();
      } else if (mode === 'exec' && window._triggerStopRun) {
        // 'exec': atomic end-and-replace — drum-pad style.
        window._triggerStopRun();
      }
      // 'show' = tab switch only, no run
    }
  }

  // Fade transition
  if (mount) {
    mount.classList.add('buf-fade-out');
    setTimeout(() => {
      finishSwitch();
      mount.classList.remove('buf-fade-out');
      mount.classList.add('buf-fade-in');
      mount.addEventListener('animationend', () => mount.classList.remove('buf-fade-in'), { once: true });
    }, 100);
    return;
  }

  finishSwitch();
}

function createTabBar() {
  const bar = document.createElement('div');
  bar.id = 'buffer-tabs';
  if (window._jamMode) bar.classList.add('autorun-on');
  // Tabs live in a no-wrap sub-flex so they always stay on a single row
  // even at narrow portrait widths. Without this, 8 large MPC-style pads
  // in jam mode wrap into 2-3 rows on iPhone widths. The wrapper itself
  // is a single flex item of #buffer-tabs (which still wraps to put
  // modeSeg on its own row above the tabs row).
  const tabsRow = document.createElement('div');
  tabsRow.className = 'buffer-tabs-row';
  for (let i = 0; i < NUM_BUFFERS; i++) {
    const tab = document.createElement('button');
    tab.className = 'buffer-tab';
    tab.dataset.idx = i;
    tab.textContent = i;
    tab.title = `Buffer ${i} (Alt+${i})`;
    // Tap → switch buffer. Drag → let panel-divider drag kick in (don't switch).
    let _tabStartX = 0, _tabStartY = 0, _tabDragged = false;
    tab.addEventListener('pointerdown', (e) => {
      _tabStartX = e.clientX;
      _tabStartY = e.clientY;
      _tabDragged = false;
    });
    tab.addEventListener('pointermove', (e) => {
      if (Math.abs(e.clientX - _tabStartX) > 8 || Math.abs(e.clientY - _tabStartY) > 8) {
        _tabDragged = true;
      }
    });
    function fireTab() {
      if (i === activeBufferIdx && window._jamMode) {
        const mode = window._jamSwitchMode;
        if (mode === 'run' && window._triggerLayerRun) window._triggerLayerRun();
        else if (mode === 'exec' && window._triggerStopRun) window._triggerStopRun();
        // 'show' on the active tab is a no-op
      } else {
        switchBuffer(i);
      }
      // MPC-style flash on trigger. Remove + reflow + add so the animation restarts on every tap.
      tab.classList.remove('tab-tapped');
      void tab.offsetWidth;
      tab.classList.add('tab-tapped');
    }
    // Jam mode wants instant pointerdown trigger (MPC-pad feel). Outside
    // Jam mode we keep pointerup so the user can still abort by dragging.
    let _firedOnDown = false;
    tab.addEventListener('pointerdown', (e) => {
      if (window._jamMode) {
        _firedOnDown = true;
        e.preventDefault();
        fireTab();
      } else {
        _firedOnDown = false;
      }
    });
    tab.addEventListener('pointerup', (e) => {
      if (_firedOnDown) { _firedOnDown = false; return; }
      if (_tabDragged) return;
      e.preventDefault();
      fireTab();
    });
    tabsRow.appendChild(tab);
    if (i < NUM_BUFFERS - 1) {
      const sep = document.createElement('span');
      sep.className = 'buffer-tab-sep';
      sep.textContent = '|';
      sep.setAttribute('aria-hidden', 'true');
      tabsRow.appendChild(sep);
    }
  }
  bar.appendChild(tabsRow);

  // ── Jam mode toggle + segmented switch-behaviour picker ──
  // `jam` is a binary on/off. When on, a segmented control with all three
  // options {auto run | switch | stop & run} is revealed to its left and
  // controls the tab-switch behaviour. The segments are mutually exclusive
  // — tapping any one selects it directly so the available options are
  // always visible.
  // run  — replaces just the editor session (restartRun "core/editor")
  // show — only switches the active tab; no audio change
  // exec — same restart today; reserved for "replace all sessions" later
  //        (Unix exec(): the running process gets replaced by a new image)
  const MODES = [
    { id: 'run',  label: 'run'  },
    { id: 'show', label: 'show' },
    { id: 'exec', label: 'exec' },
  ];

  // No wrapper — modeSeg + jamToggle are direct flex children of #buffer-tabs
  // so flex-wrap can spill them onto a new row when room runs out. JAM is
  // pinned right via CSS margin-left:auto and stays inline with tabs;
  // modeSeg follows and wraps to the next row at narrow widths.

  const modeSeg = document.createElement('div');
  modeSeg.className = 'mode-segmented' + (window._jamMode ? ' visible' : '');
  modeSeg.setAttribute('title', 'Switch behaviour on buffer tap');

  const segEls = {};
  for (const m of MODES) {
    const seg = document.createElement('div');
    seg.className = 'mode-seg' + (window._jamSwitchMode === m.id ? ' active' : '');
    seg.dataset.mode = m.id;
    seg.textContent = m.label;
    modeSeg.appendChild(seg);
    segEls[m.id] = seg;
  }

  const jamToggle = document.createElement('div');
  jamToggle.className = 'autorun-toggle jam-toggle' + (window._jamMode ? ' active' : '');
  jamToggle.setAttribute('title', 'Jam mode — tap a buffer tab to trigger your chosen switch behaviour');
  jamToggle.textContent = 'jam';

  function installTapHandler(el, onTap) {
    let sx = 0, sy = 0, dragged = false;
    el.addEventListener('pointerdown', (e) => { sx = e.clientX; sy = e.clientY; dragged = false; });
    el.addEventListener('pointermove', (e) => {
      if (Math.abs(e.clientX - sx) > 8 || Math.abs(e.clientY - sy) > 8) dragged = true;
    });
    el.addEventListener('pointerup', (e) => {
      if (dragged) return;
      e.preventDefault();
      onTap();
    });
  }

  function _placeJamForMode() {
    // jam-off: JAM sits at the right of the tabs row (9th flex item).
    // jam-on: JAM moves up to share the controls row with modeSeg —
    // gives it more breathing room on iPhone widths where a 9th tab
    // gets squeezed off the screen, and visually groups the control
    // surface (modeSeg + JAM toggle) on its own row. The .in-modeseg-row
    // class flips order:-1 so JAM joins the modeSeg flex row instead of
    // staying at default order:0 (which would put it on its own row).
    if (window._jamMode) {
      modeSeg.parentElement?.insertBefore(jamToggle, modeSeg.nextSibling);
      jamToggle.classList.add('in-modeseg-row');
    } else {
      tabsRow.appendChild(jamToggle);
      jamToggle.classList.remove('in-modeseg-row');
    }
  }

  installTapHandler(jamToggle, () => {
    window._jamMode = !window._jamMode;
    jamToggle.classList.toggle('active', window._jamMode);
    modeSeg.classList.toggle('visible', window._jamMode);
    bar.classList.toggle('autorun-on', window._jamMode);
    _placeJamForMode();
  });

  for (const m of MODES) {
    installTapHandler(segEls[m.id], () => {
      if (!window._jamMode) return;
      window._jamSwitchMode = m.id;
      for (const k of MODES) {
        segEls[k.id].classList.toggle('active', k.id === m.id);
      }
    });
  }

  // Layout: #buffer-tabs is a flex row with `justify-content: center` and
  // BALANCED 80px left/right padding. JAM is absolute-positioned within
  // the right padding band so it doesn't take flex space (auto-margins
  // there would push the centered tabs leftward).
  //
  // The mode picker (when jam is on) needs to live on its own row ABOVE
  // the tabs row so the tabs stay anchored to the bar's bottom edge —
  // that keeps the JAM toggle (also bottom-anchored) at a constant page
  // y across jam-on/jam-off.
  //
  // Achieved with TWO line-break spacers AND order:-1 on the modeSeg
  // group, so visual flex order is:
  //   break-before  (full row, invisible) → modeSeg (108px row) →
  //   break-after   (full row, invisible) → tabs (its own row)
  // Without break-after the tabs would share modeSeg's row at wide
  // widths because 108 + tabs fits within the row.
  const modeSegBreakBefore = document.createElement('div');
  modeSegBreakBefore.className = 'mode-seg-break';
  const modeSegBreakAfter = document.createElement('div');
  modeSegBreakAfter.className = 'mode-seg-break';
  // JAM is the 9th item in the tabs flex row — same width as the tabs,
  // shrinks/grows with them. modeSeg + breaks remain on #buffer-tabs so
  // they can wrap to their own row above via order:-1.
  tabsRow.appendChild(jamToggle);
  bar.appendChild(modeSegBreakBefore);
  bar.appendChild(modeSeg);
  bar.appendChild(modeSegBreakAfter);
  // Move JAM to the modeSeg row if jam mode is already on at startup.
  _placeJamForMode();

  return bar;
}

function updateTabBar() {
  const tabs = document.querySelectorAll('.buffer-tab');
  tabs.forEach((tab) => {
    const i = parseInt(tab.dataset.idx, 10);
    tab.classList.toggle('active', i === activeBufferIdx);
    const hasContent = bufferStates[i]
      ? bufferStates[i].doc.length > 0
      : (loadBufferSource(i) !== null && loadBufferSource(i).length > 0);
    tab.classList.toggle('has-content', hasContent);
  });
}

function createEditor(mountEl) {
  migrateStorage();
  seedDemoBuffersIfNew();
  activeBufferIdx = loadActiveBufferIdx();

  const saved = loadBufferSource(activeBufferIdx);
  const doc = saved !== null ? saved : (activeBufferIdx === 0 ? DEFAULT_SOURCE : '');
  const startState = createBufferState(doc);
  bufferStates[activeBufferIdx] = startState;

  view = new EditorView({
    state: startState,
    parent: mountEl,
  });

  // Suppress iOS QuickType bar and system keyboard on touch devices
  var cmContent = view.contentDOM;
  cmContent.setAttribute('autocorrect', 'off');
  cmContent.setAttribute('autocapitalize', 'none');
  cmContent.setAttribute('autocomplete', 'off');
  cmContent.setAttribute('spellcheck', 'false');
  if (window.matchMedia('(pointer: coarse)').matches) {
    cmContent.setAttribute('inputmode', 'none');
  }

  // Ctrl-N/P — completion nav when dropdown open, cursor movement otherwise
  view.dom.addEventListener('keydown', (e) => {
    if (!e.ctrlKey || (e.key !== 'n' && e.key !== 'p')) return;
    if (completionStatus(view.state) === 'active') {
      e.preventDefault();
      e.stopPropagation();
      moveCompletionSelection(e.key === 'n')(view);
    }
    // Otherwise let it through for normal cursor up/down
  }, true);

  return view;
}

// ── Public API ──

window.editor = {
  getSource() {
    return view ? view.state.doc.toString() : '';
  },

  _refreshBlockCursor() { refreshBlockCursor(); },

  _showSampleCtx() {
    if (view) updateSampleLineInfo(view.state);
  },

  _showSampleCtxForName(name) {
    // Skip synths
    if (document.getElementById('synth-' + name)) return false;
    // Only try if it looks like a sample: has _ (built-in convention) or is in user index or already analysed
    const isMaybeSample = name.includes('_') ||
      (window._userSampleIndex && window._userSampleIndex[name]) ||
      (window._sampleMeta && window._sampleMeta[name]);
    if (!isMaybeSample) return false;
    const el = document.getElementById('ctx-info');
    if (!el) return false;
    el.innerHTML = renderSampleCtxInfo(name, ':' + name);
    return true;
  },

  refreshDecorations() {
    if (!view || !lastResultMsg) return;
    const decos = buildDecorations(lastResultMsg.leaves, lastResultMsg.errors, view.state.doc, false);
    view.dispatch({ effects: [setDecorations.of(decos)] });
  },

  getLastResult() {
    return lastResultMsg;
  },

  format() {
    if (!worker) return Promise.resolve();
    return new Promise(resolve => {
      formatResolve = resolve;
      worker.postMessage({ type: 'format' });
      // Worker only replies if code changed — timeout as fallback
      setTimeout(() => { if (formatResolve) { formatResolve(); formatResolve = null; } }, 100);
    });
  },

  highlightStatement(id) {
    highlightStatement(id);
  },

  setFlashMap(entries) {
    flashMap.clear();
    activeFlashes.forEach(f => clearTimeout(f.timer));
    activeFlashes.clear();
    if (entries) {
      for (const entry of entries) {
        flashMap.set(entry.id, { start: entry.start, end: entry.end });
      }
      // Build stmt→loop mapping for event routing
      if (view) {
        // Don't clear stmtToLoop — old entries keep working until old VM stops
        const doc = view.state.doc;
        // Find live_loop blocks and map contained stmt_ids to loop names
        const loopRanges = [];
        for (const entry of entries) {
          const text = doc.sliceString(entry.start, Math.min(entry.start + 40, doc.length));
          const match = text.match(/^live_loop\s+:(\w+)/);
          if (match) loopRanges.push({ name: match[1], start: entry.start, end: entry.end });
        }
        for (const entry of entries) {
          for (const lr of loopRanges) {
            if (entry.start >= lr.start && entry.start < lr.end) {
              // Statement is inside a live_loop
              stmtToLoop.set(entry.id, lr.name);
            } else if (lr.start >= entry.start && lr.start < entry.end) {
              // Statement contains a live_loop (e.g. with_fx wrapping a live_loop)
              stmtToLoop.set(entry.id, lr.name);
            }
          }
        }
        // Defer to avoid dispatching during setFlashMap's update cycle
        if (loopRanges.length > 0) setTimeout(() => startLiveLoopBars(), 0);
      }
    }
  },

  stopLiveLoopBars() {
    stopLiveLoopBars();
  },

  setRollBPM(bpm) { rollBPM = bpm; },

  pushLoopEvent(stmtId, color, dur, note) {
    const loopName = stmtToLoop.get(stmtId);
    if (!loopName) return;
    const events = loopEvents.get(loopName) || [];
    const now = performance.now();
    // dur=null means open-ended (alive until endLoopEvent closes it via stmtId)
    events.push({ stmtId, color, time: now, dur: dur, endTime: dur ? now + dur : null, note: note ?? -1 });
    // Trim ended events outside the visible window (open events stay until /n_end)
    const rollWindowMs = ROLL_BEATS * (60000 / rollBPM);
    const cutoff = now - rollWindowMs - 2000;
    for (let i = events.length - 1; i >= 0; i--) {
      if (events[i].endTime && events[i].endTime < cutoff) events.splice(i, 1);
    }
    loopEvents.set(loopName, events);
  },

  // End the oldest still-alive event for a given stmt_id (called on /n_end)
  endLoopEvent(stmtId) {
    const loopName = stmtToLoop.get(stmtId);
    if (!loopName) return;
    const events = loopEvents.get(loopName);
    if (!events) return;
    const now = performance.now();
    for (const ev of events) {
      if (!ev.endTime && ev.stmtId === stmtId) {
        ev.endTime = now;
        return;
      }
    }
  },

  setReadOnly(on) {
    if (!view) return;
    view.dispatch({
      effects: readOnlyCompartment.reconfigure(EditorState.readOnly.of(on)),
    });
  },

  get view() { return view; },

  switchBuffer(idx) { switchBuffer(idx); },
  get activeBuffer() { return activeBufferIdx; },
  saveAll() { saveAllBuffers(); },

  // Commands for touch keyboard toolbar
  cmd: { undo, redo, indentMore, indentLess, cursorLineUp, cursorLineDown, cursorCharLeft, cursorCharRight, startCompletion, acceptCompletion, completionStatus },

  DEFAULT_SOURCE,
  DEMO_BUFFERS,

  resetAllBuffers(bufferMap) {
    // bufferMap: { 0: 'code', 1: 'code', ... } or a string (buffer 0 only)
    if (typeof bufferMap === 'string') bufferMap = { 0: bufferMap };
    for (let i = 0; i < NUM_BUFFERS; i++) {
      bufferStates[i] = null;
      saveBufferSource(i, bufferMap[i] || '');
    }
    activeBufferIdx = 0;
    saveActiveBufferIdx(0);
    if (view) {
      view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: bufferMap[0] || '' } });
    }
    updateTabBar();
  },
};

// Reset cached scope background on theme change
window._resetScopeBg = () => {
  for (const name in loopWidgetElements) {
    delete loopWidgetElements[name]._scopeBgRgb;
  }
};

// ── Boot ──

const mountEl = document.getElementById('editor-mount');
if (mountEl) {
  // Insert tab bar — always below editor inside editor-column
  const tabBar = createTabBar();
  const editorColumn = document.getElementById('editor-column');
  if (editorColumn) editorColumn.appendChild(tabBar);

  initWorker();
  createEditor(mountEl);
  updateTabBar();

  // Mirror ctx-info to interaction panel Info tab
  const ctxInfoEl = document.getElementById('ctx-info');
  const panelInfoContent = document.getElementById('panel-info-content');
  if (ctxInfoEl && panelInfoContent) {
    const observer = new MutationObserver(() => {
      panelInfoContent.innerHTML = ctxInfoEl.innerHTML;
    });
    observer.observe(ctxInfoEl, { childList: true, subtree: true, characterData: true });
    // Delegate play button clicks on mirrored content
    panelInfoContent.addEventListener('mousedown', (e) => {
      e.preventDefault();
      const btn = e.target.closest('.ctx-play-btn');
      if (btn && btn.dataset.sample && window._previewSample) {
        window._previewSample(btn.dataset.sample);
      }
    });
  }
}
